/**
 * ObrasProjectosPage - Módulo de Obras/Projectos Técnicos
 * ========================================================
 * 
 * Diretiva P0 - Requisitos:
 * - Abertura de Obra com todos os campos
 * - Formulários específicos por tipo de serviço (GPS, CCTV, etc.)
 * - Detalhes de execução obrigatórios (horas, técnico, local)
 * - Assinatura digital funcional (persistência + PDF)
 * - Impressão bloqueada até assinatura + detalhes de execução
 * - Documento pendente marcado como "SEM ASSINATURA"
 * - PDF profissional com logotipo e todos os campos
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import SignaturePad from 'signature_pad';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProductCombobox } from '@/components/ProductCombobox';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getStoredAuthToken } from '@/lib/authStorage';
import { applyCleanOrLegacyParam, buildHandleMaps, resolveHandleOrLegacyId } from '@/lib/queryHandles';
import {
  HardHat, Plus, Search, RefreshCw, Edit2, Eye, MapPin, User,
  Phone, Mail, Calendar, CheckCircle, Circle, Truck, Fuel, Camera,
  Network, Package, ClipboardList, Users, Pen, Printer, FileText,
  AlertTriangle, Clock, Play, Flag, Trash2, Download, XCircle, Save,
  Settings, Wifi, Lock, Car, Video, Timer, Store, Link2, Copy, ShieldCheck, History
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const parseApiResponse = async (response) => {
  const rawText = await response.text();
  let data = {};

  if (rawText) {
    try {
      data = JSON.parse(rawText);
    } catch {
      data = { detail: rawText };
    }
  }

  if (!response.ok) {
    throw new Error(data.detail || data.message || 'Error');
  }

  return data;
};

const api = {
  get: async (url) => {
    const token = getStoredAuthToken();
    const res = await fetch(`${API_URL}${url}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    return parseApiResponse(res);
  },
  post: async (url, data) => {
    const token = getStoredAuthToken();
    const res = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return parseApiResponse(res);
  },
  put: async (url, data) => {
    const token = getStoredAuthToken();
    const res = await fetch(`${API_URL}${url}`, {
      method: 'PUT',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    return parseApiResponse(res);
  },
  delete: async (url, data = null) => {
    const token = getStoredAuthToken();
    const res = await fetch(`${API_URL}${url}`, {
      method: 'DELETE',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json'
      },
      body: data ? JSON.stringify(data) : undefined,
    });
    return parseApiResponse(res);
  }
};

const PROJECT_TYPES = {
  gps_fleet: { label: 'GPS / Gestão de Frota', icon: Truck, color: 'bg-blue-100 text-blue-800' },
  fuel_management: { label: 'Gestão de Combustíveis', icon: Fuel, color: 'bg-amber-100 text-amber-800' },
  cctv: { label: 'CCTV / Videovigilância', icon: Camera, color: 'bg-purple-100 text-purple-800' },
  access_control: { label: 'Controlo de Acesso', icon: Users, color: 'bg-green-100 text-green-800' },
  networking: { label: 'Redes e Infraestrutura', icon: Network, color: 'bg-cyan-100 text-cyan-800' },
  other: { label: 'Outros Serviços', icon: Package, color: 'bg-gray-100 text-gray-800' }
};

const PROJECT_STATUS = {
  draft: { label: 'Rascunho', color: 'bg-gray-100 text-gray-800' },
  open: { label: 'Aberto', color: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'Em Execução', color: 'bg-yellow-100 text-yellow-800' },
  pending_signature: { label: 'Aguarda Assinatura', color: 'bg-orange-100 text-orange-800' },
  complementary: { label: 'Complementar', color: 'bg-violet-100 text-violet-800' },
  completed: { label: 'Concluído', color: 'bg-green-100 text-green-800' },
  delivered: { label: 'Entregue', color: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800' }
};

const EMPTY_MATERIAL_FORM = {
  id: null,
  product_id: '',
  item_id: '',
  description: '',
  quantity: 1,
  unit: 'un',
  serial_number: '',
  notes: '',
};

const GPS_OPERATION_OPTIONS = [
  { value: 'installation', label: 'Instalação' },
  { value: 'uninstallation', label: 'Desinstalação' },
  { value: 'maintenance', label: 'Manutenção / Assistência Técnica' },
  { value: 'reinstallation', label: 'Reinstalação' },
  { value: 'replacement', label: 'Substituição de Equipamento' },
  { value: 'diagnostic', label: 'Diagnóstico / Inspecção Técnica' },
  { value: 'temporary_removal', label: 'Remoção Temporária' },
  { value: 'vehicle_transfer', label: 'Transferência entre Viaturas' },
  { value: 'reconfiguration', label: 'Actualização / Reconfiguração' },
  { value: 'other', label: 'Outro' },
];

const GPS_INSTALLATION_TYPE_OPTIONS = [
  { value: 'new_installation', label: 'Nova Instalação' },
  { value: 'replacement_installation', label: 'Instalação por Substituição' },
  { value: 'reinstallation', label: 'Reinstalação' },
  { value: 'partial_installation', label: 'Instalação Parcial' },
  { value: 'complete_installation', label: 'Instalação Completa' },
  { value: 'new_fleet_installation', label: 'Instalação em Frota Nova' },
  { value: 'used_vehicle_installation', label: 'Instalação em Viatura Usada' },
  { value: 'other', label: 'Outro' },
];

const GPS_UNINSTALLATION_TYPE_OPTIONS = [
  { value: 'permanent_uninstallation', label: 'Desinstalação Definitiva' },
  { value: 'temporary_uninstallation', label: 'Desinstalação Temporária' },
  { value: 'transfer_uninstallation', label: 'Desinstalação para Transferência' },
  { value: 'replacement_uninstallation', label: 'Desinstalação para Substituição' },
  { value: 'fault_uninstallation', label: 'Desinstalação por Avaria' },
  { value: 'other', label: 'Outro' },
];

const GPS_INSTALLATION_OPERATION_TYPES = ['installation', 'reinstallation', 'replacement', 'vehicle_transfer', 'reconfiguration'];

const DEFAULT_GPS_FIELDS = {
  operation_type: 'installation',
  operation_other_description: '',
  installation_type: 'new_installation',
  uninstallation_type: '',
  vehicle_plate: '',
  vehicle_brand: '',
  vehicle_model: '',
  fleet_number: '',
  vehicle_type: '',
  operational_center: '',
  meter_reading: '',
  gps_equipment_type: '',
  equipment_brand_model: '',
  imei_gps: '',
  serial_number: '',
  sim_card_number: '',
  equipment_condition_before: '',
  equipment_supplier: '',
  intervention_reason: '',
  final_customer_department: '',
  installation_location: '',
  intervention_site: '',
  planned_date: '',
  priority: 'normal',
  requires_travel: false,
  requires_final_tests: false,
  uninstall_equipment_destination: '',
  uninstall_requires_reinstallation: false,
  technical_observations: '',
  materials_used: '',
  requires_return: false,
  final_service_status: '',
  communication_test: 'pending',
  notes: '',
};

const buildGpsQuestionnairePreview = (fields = {}) => {
  const operationType = fields.operation_type || 'installation';
  const base = [
    'Confirmar dados da viatura / activo (matrícula, marca, modelo e frota)',
    'Validar equipamento GPS, IMEI/serial e SIM number',
    'Registar motivo, local, prioridade e responsável da intervenção',
  ];
  const installation = [
    'Alimentação confirmada',
    'Ponto de ignição confirmado',
    'Fixação concluída',
    'Cablagem protegida',
    'GPS a comunicar',
    'Teste de ignição realizado',
    'Teste de localização realizado',
    'Fotos captadas',
    'Cliente validou intervenção',
  ];
  const uninstallation = [
    'Equipamento removido sem danos',
    'Cablagem removida ou isolada correctamente',
    'Estado do equipamento à saída registado',
    'Serial validado',
    'Destino do equipamento registado',
    'Necessita reinstalação posterior?',
    'Fotos da remoção captadas',
    'Cliente validou intervenção',
  ];
  const maintenance = [
    'Executar diagnóstico técnico do equipamento GPS',
    'Validar comunicação, alimentação e cablagem',
    'Registar peças / materiais usados',
    'Confirmar funcionamento final com responsável do cliente',
  ];
  const map = {
    installation: [...base, ...installation],
    uninstallation: [...base, ...uninstallation],
    maintenance: [...base, ...maintenance],
    reinstallation: [...base, ...uninstallation.slice(0, 5), ...installation],
    replacement: [...base, ...uninstallation.slice(0, 5), ...installation],
    diagnostic: [...base, ...maintenance],
    temporary_removal: [...base, ...uninstallation],
    vehicle_transfer: [...base, ...uninstallation.slice(0, 5), ...installation],
    reconfiguration: [...base, ...maintenance],
    other: [...base, 'Descrever manualmente o cenário operacional e o resultado esperado'],
  };
  return map[operationType] || map.installation;
};

const getGpsOptionLabel = (options, value) => options.find((option) => option.value === value)?.label || value || '—';

const getGpsOptionLabelOrEmpty = (options, value) => {
  if (!value) return '';
  return options.find((option) => option.value === value)?.label || value;
};

const getGpsDetailItems = (fields = {}) => {
  const operationType = fields.operation_type || 'installation';
  return [
    ['Tipo de Operação', getGpsOptionLabelOrEmpty(GPS_OPERATION_OPTIONS, operationType)],
    operationType === 'other' ? ['Descrição manual', fields.operation_other_description] : null,
    GPS_INSTALLATION_OPERATION_TYPES.includes(operationType)
      ? ['Tipo de Instalação', getGpsOptionLabelOrEmpty(GPS_INSTALLATION_TYPE_OPTIONS, fields.installation_type)]
      : null,
    operationType === 'uninstallation'
      ? ['Campo de Desinstalação', getGpsOptionLabelOrEmpty(GPS_UNINSTALLATION_TYPE_OPTIONS, fields.uninstallation_type)]
      : null,
    ['Matrícula', fields.vehicle_plate],
    ['Marca', fields.vehicle_brand],
    ['Modelo', fields.vehicle_model],
    ['Número da Frota', fields.fleet_number],
    ['Tipo de Viatura', fields.vehicle_type],
    ['Centro Operacional', fields.operational_center],
    ['Equipamento GPS', fields.gps_equipment_type],
    ['Marca / Modelo do Equipamento', fields.equipment_brand_model],
    ['IMEI', fields.imei_gps],
    ['Serial Number', fields.serial_number],
    ['SIM Number', fields.sim_card_number],
    ['Estado antes da intervenção', fields.equipment_condition_before],
    ['Motivo da intervenção', fields.intervention_reason],
    ['Cliente final / Departamento', fields.final_customer_department],
    ['Local da intervenção', fields.intervention_site || fields.installation_location],
    ['Destino do equipamento', fields.uninstall_equipment_destination],
    ['Prioridade', fields.priority],
    ['Estado final do serviço', fields.final_service_status],
    ['Observações técnicas', fields.technical_observations],
    ['Peças / materiais usados', fields.materials_used],
  ].filter((item) => item && item[1]);
};

export default function ObrasProjectosPage() {
  const { hasRole } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const emptyInitialDetailsForm = {
    description: '',
    technical_description: '',
    scope_of_work: '',
    internal_notes: '',
  };
  const [projects, setProjects] = useState([]);
  const projectHandleMaps = useMemo(() => buildHandleMaps(projects, (project) => project.client_name || project.title || project.id), [projects]);
  const [productCatalog, setProductCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  // Dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, project: null });
  const [viewInitialDetailsEditor, setViewInitialDetailsEditor] = useState({
    editing: false,
    saving: false,
    form: emptyInitialDetailsForm,
  });
  const [viewInitialDetailsHistory, setViewInitialDetailsHistory] = useState([]);
  const [conversionLoadingKey, setConversionLoadingKey] = useState(null);
  const [signatureDialog, setSignatureDialog] = useState({ open: false, project: null, role: 'client' });
  const [checklistDialog, setChecklistDialog] = useState({ open: false, project: null });
  const [printDialog, setPrintDialog] = useState({ open: false, project: null, canPrint: false });
  const [executionDialog, setExecutionDialog] = useState({ open: false, project: null });
  const [typeFieldsDialog, setTypeFieldsDialog] = useState({ open: false, project: null });
  const [storeDialog, setStoreDialog] = useState({ open: false, project: null, storeId: '' });
  const [portalDialog, setPortalDialog] = useState({
    open: false,
    project: null,
    allowedEmailsRaw: '',
    expiresInDays: 7,
    generatedLink: '',
    generatedPath: '',
    expiresAt: '',
    creating: false,
  });
  const [conversionDialog, setConversionDialog] = useState({
    open: false,
    loading: false,
    submitting: false,
    project: null,
    location: null,
    items: [],
    warnings: [],
    suggestedNotes: '',
    additionalNotes: '',
    vatOptions: [],
  });
  const [duplicateServiceDialog, setDuplicateServiceDialog] = useState({
    open: false,
    loading: false,
    submitting: false,
    project: null,
    location: null,
    item: null,
    suggestedReason: '',
    revisitReason: '',
    additionalNotes: '',
    interventionDate: '',
    startTime: '',
    endTime: '',
    technicianName: '',
    vatOptions: [],
  });
  const [materialDialog, setMaterialDialog] = useState({
    open: false,
    mode: 'create',
    project: null,
    saving: false,
    form: EMPTY_MATERIAL_FORM,
  });
  const [deleteMaterialDialog, setDeleteMaterialDialog] = useState({
    open: false,
    project: null,
    material: null,
    deleting: false,
  });
  const [historyEntryDialog, setHistoryEntryDialog] = useState({
    open: false,
    project: null,
    entry: null,
    saving: false,
    form: {
      display_title: '',
      admin_notes: '',
      changed_by_name: '',
      changed_at: '',
      changed_fields_text: '',
    },
  });
  const [deleteHistoryEntryDialog, setDeleteHistoryEntryDialog] = useState({
    open: false,
    project: null,
    entry: null,
    deleting: false,
  });
  const [interventionDialog, setInterventionDialog] = useState({
    open: false,
    project: null,
    entry: null,
    saving: false,
    form: {
      description: '',
      reason: '',
      notes: '',
      intervention_date: '',
      start_time: '',
      end_time: '',
      technician_name: '',
    },
  });
  const [deleteInterventionDialog, setDeleteInterventionDialog] = useState({
    open: false,
    project: null,
    entry: null,
    deleting: false,
  });
  const [deleteProjectDialog, setDeleteProjectDialog] = useState({
    open: false,
    project: null,
    deleting: false,
  });
  const [integrityDialog, setIntegrityDialog] = useState({ open: false, loading: false, project: null, report: null });

  // Clients for autocomplete
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [storeLocations, setStoreLocations] = useState([]);

  // Signature pad ref
  const signatureCanvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const autoOpenedProjectRef = useRef(null);
  const closingProjectRef = useRef(null);
  const openViewProjectDialogRef = useRef(null);
  const [signatureSignerName, setSignatureSignerName] = useState('');
  const canClearProjectSignatures = hasRole(['admin', 'store_manager']);
  const canDeleteProjects = hasRole(['admin', 'store_manager']);
  const canManageComplementaryItems = hasRole(['admin', 'store_manager', 'technician', 'tech_lead']);
  const canManageVisibleHistory = hasRole(['admin', 'store_manager']);
  const canManageInterventions = hasRole(['admin', 'store_manager', 'technician', 'tech_lead']);
  const inventoryStyleActionButtonClass = 'h-9 rounded-xl border-slate-300 bg-white px-3 text-slate-700 shadow-sm transition-transform hover:-translate-y-0.5 hover:border-slate-400 hover:bg-slate-50';
  const inventoryStyleDangerButtonClass = 'h-9 rounded-xl border-red-300 bg-white px-3 text-red-700 shadow-sm transition-transform hover:-translate-y-0.5 hover:border-red-400 hover:bg-red-50';

  // Execution details form
  const [executionForm, setExecutionForm] = useState({
    execution_date: '',
    execution_end_date: '',
    start_time: '',
    end_time: '',
    work_order: '',
    technicians_names: [''],
    store_id: '',
    execution_location: '',
    final_observations: '',
    work_segments: [{ work_date: '', start_time: '', end_time: '' }]
  });

  // Type-specific fields
  const [typeFields, setTypeFields] = useState({});
  const [applyChecklistTemplate, setApplyChecklistTemplate] = useState(false);

  // Form state
  const [form, setForm] = useState({
    project_type: 'gps_fleet',
    title: '',
    description: '',
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    store_id: '',
    work_location: '',
    work_address: '',
    work_city: 'Maputo',
    gps_coordinates: '',
    technical_description: '',
    scope_of_work: '',
    scheduled_start_date: '',
    scheduled_end_date: '',
    internal_notes: '',
    gps_fields: { ...DEFAULT_GPS_FIELDS },
  });

  const resetForm = () => {
    setForm({
      project_type: 'gps_fleet',
      title: '',
      description: '',
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      store_id: '',
      work_location: '',
      work_address: '',
      work_city: 'Maputo',
      gps_coordinates: '',
      technical_description: '',
      scope_of_work: '',
      scheduled_start_date: '',
      scheduled_end_date: '',
      internal_notes: '',
      gps_fields: { ...DEFAULT_GPS_FIELDS },
    });
    setClientSearch('');
  };

  const syncProjectQueryParam = (projectId) => {
    const params = new URLSearchParams(searchParams);
    params.delete('project');
    params.delete('project_id');
    applyCleanOrLegacyParam({
      params,
      cleanKey: 'project',
      legacyKey: 'project_id',
      value: projectId,
      idToHandle: projectHandleMaps.idToHandle,
    });
    setSearchParams(params, { replace: true });
  };

  const closeViewProjectDialog = () => {
    const currentProjectId = viewDialog.project?.id || resolveHandleOrLegacyId({ cleanValue: searchParams.get('project'), legacyValue: searchParams.get('project_id'), handleToId: projectHandleMaps.handleToId });
    const queryProjectId = resolveHandleOrLegacyId({ cleanValue: searchParams.get('project'), legacyValue: searchParams.get('project_id'), handleToId: projectHandleMaps.handleToId });
    setViewDialog({ open: false, project: null });
    setViewInitialDetailsEditor({ editing: false, saving: false, form: emptyInitialDetailsForm });
    setViewInitialDetailsHistory([]);
    if (queryProjectId && currentProjectId && queryProjectId === currentProjectId) {
      closingProjectRef.current = currentProjectId;
      autoOpenedProjectRef.current = currentProjectId;
      syncProjectQueryParam(null);
      return;
    }
    autoOpenedProjectRef.current = null;
    closingProjectRef.current = null;
  };

  const openViewProjectDialog = async (projectOrId, options = {}) => {
    const projectId = typeof projectOrId === 'string' ? projectOrId : projectOrId?.id;
    if (!projectId) return;
    const { syncUrl = true } = options;

    try {
      closingProjectRef.current = null;
      const queryProjectId = resolveHandleOrLegacyId({ cleanValue: searchParams.get('project'), legacyValue: searchParams.get('project_id'), handleToId: projectHandleMaps.handleToId });
      if (syncUrl && queryProjectId !== projectId) {
        syncProjectQueryParam(projectId);
      }
      const [project, history] = await Promise.all([
        api.get(`/api/projects/${projectId}`),
        api.get(`/api/projects/${projectId}/initial-details-history`),
      ]);
      setViewDialog({ open: true, project });
      setViewInitialDetailsHistory(history || []);
      setViewInitialDetailsEditor({
        editing: false,
        saving: false,
        form: {
          description: project.description || '',
          technical_description: project.technical_description || '',
          scope_of_work: project.scope_of_work || '',
          internal_notes: project.internal_notes || '',
        },
      });
      autoOpenedProjectRef.current = projectId;
    } catch (error) {
      setViewInitialDetailsHistory([]);
      toast.error('Erro ao carregar detalhes da obra');
    }
  };

  openViewProjectDialogRef.current = openViewProjectDialog;

  const cancelInitialDetailsEditing = () => {
    setViewInitialDetailsEditor({
      editing: false,
      saving: false,
      form: {
        description: viewDialog.project?.description || '',
        technical_description: viewDialog.project?.technical_description || '',
        scope_of_work: viewDialog.project?.scope_of_work || '',
        internal_notes: viewDialog.project?.internal_notes || '',
      },
    });
  };

  useEffect(() => {
    const projectIdFromQuery = resolveHandleOrLegacyId({ cleanValue: searchParams.get('project'), legacyValue: searchParams.get('project_id'), handleToId: projectHandleMaps.handleToId });
    if (!projectIdFromQuery || loading) {
      if (!projectIdFromQuery) {
        autoOpenedProjectRef.current = null;
        closingProjectRef.current = null;
      }
      return;
    }

    if (!viewDialog.open && closingProjectRef.current === projectIdFromQuery) {
      return;
    }

    if (viewDialog.open && viewDialog.project?.id === projectIdFromQuery) {
      autoOpenedProjectRef.current = projectIdFromQuery;
      return;
    }

    if (autoOpenedProjectRef.current === projectIdFromQuery) {
      return;
    }

    autoOpenedProjectRef.current = projectIdFromQuery;
    openViewProjectDialogRef.current?.(projectIdFromQuery, { syncUrl: false });
  }, [loading, searchParams, viewDialog.open, viewDialog.project?.id, projectHandleMaps.handleToId]);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      let url = '/api/projects/?limit=100';
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      if (filterType !== 'all') url += `&project_type=${filterType}`;
      const data = await api.get(url);
      setProjects(data || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar projectos');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  const loadClients = useCallback(async () => {
    try {
      const data = await api.get('/api/clients/active');
      setClients(data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  }, []);

  const loadStores = useCallback(async () => {
    try {
      const data = await api.get('/api/settings/locations');
      const validStores = (data || []).filter((location) => !location.is_deleted);
      setStoreLocations(validStores);
    } catch (error) {
      console.error('Error loading stores:', error);
      toast.error('Erro ao carregar lojas/localizações');
    }
  }, []);

  const loadProductCatalog = useCallback(async () => {
    try {
      const data = await api.get('/api/inventory/products');
      setProductCatalog(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading product catalog:', error);
      toast.error('Erro ao carregar catálogo de artigos');
    }
  }, []);

  useEffect(() => {
    loadProjects();
    loadClients();
    loadStores();
    loadProductCatalog();
  }, [loadProjects, loadClients, loadStores, loadProductCatalog]);

  const refreshProjectsData = useCallback(async () => {
    await loadProjects();
  }, [loadProjects]);

  const availableStores = useMemo(
    () => storeLocations.filter((location) => (location.type || '') !== 'warehouse'),
    [storeLocations]
  );

  // Initialize signature pad when dialog opens
  useEffect(() => {
    let initTimer = null;
    let resizeObserver = null;
    
    if (signatureDialog.open) {
      const initSignaturePad = () => {
        const canvas = signatureCanvasRef.current;
        if (!canvas) {
          console.log('Canvas not found, retrying...');
          return;
        }
        
        // Get actual rendered dimensions
        const rect = canvas.getBoundingClientRect();
        const width = rect.width || 400;
        const height = rect.height || 160;
        
        console.log('Initializing SignaturePad with dimensions:', width, height);
        
        // Clear any previous instance first
        if (signaturePadRef.current) {
          signaturePadRef.current.off();
          signaturePadRef.current = null;
        }
        
        // Set canvas dimensions for high DPI
        const ratio = window.devicePixelRatio || 1;
        canvas.width = width * ratio;
        canvas.height = height * ratio;
        
        // Scale context for high DPI
        const ctx = canvas.getContext('2d');
        ctx.scale(ratio, ratio);
        
        // Keep transparent background to match technical/client signature layout
        ctx.clearRect(0, 0, width, height);
        
        // Initialize SignaturePad
        signaturePadRef.current = new SignaturePad(canvas, {
          backgroundColor: 'rgba(255, 255, 255, 0)',
          penColor: 'rgb(0, 0, 0)',
          minWidth: 0.5,
          maxWidth: 2.5,
          throttle: 16,
          velocityFilterWeight: 0.7
        });
        
        console.log('SignaturePad initialized successfully');
      };
      
      // Try multiple times to ensure DOM is ready
      initTimer = setTimeout(() => {
        initSignaturePad();
        // Try again after a longer delay
        setTimeout(initSignaturePad, 200);
        setTimeout(initSignaturePad, 500);
      }, 100);
    }
    
    return () => {
      if (initTimer) clearTimeout(initTimer);
      if (resizeObserver) resizeObserver.disconnect();
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
    };
  }, [signatureDialog.open]);

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }
    if (!form.customer_id) {
      toast.error('Seleccione um cliente');
      return;
    }
    if (!form.work_location.trim()) {
      toast.error('Local da obra é obrigatório');
      return;
    }
    if (!form.store_id) {
      toast.error('Seleccione a loja de origem da obra');
      return;
    }
    if (form.project_type === 'gps_fleet' && form.gps_fields?.operation_type === 'other' && !form.gps_fields?.operation_other_description?.trim()) {
      toast.error('Descrição manual é obrigatória quando o Tipo de Operação é Outro');
      return;
    }
    
    try {
      const payload = {
        ...form,
        gps_fields: form.project_type === 'gps_fleet' ? form.gps_fields : null,
      };
      const result = await api.post('/api/projects/', payload);
      toast.success(`Projecto criado: ${result.project_number || 'OK'}`);
      setCreateDialog(false);
      resetForm();
      await refreshProjectsData();
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await api.put(`/api/projects/${projectId}/status`, { status: newStatus });
      toast.success('Estado actualizado');
      await refreshProjectsData();
      if (viewDialog.project?.id === projectId) {
        await openViewProjectDialog(projectId);
      }
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleSaveInitialDetails = async () => {
    if (!viewDialog.project?.id) return;

    try {
      setViewInitialDetailsEditor((prev) => ({ ...prev, saving: true }));
      await api.put(`/api/projects/${viewDialog.project.id}`, viewInitialDetailsEditor.form);
      toast.success('Apontamentos iniciais actualizados');
      await openViewProjectDialog(viewDialog.project.id);
      await refreshProjectsData();
    } catch (error) {
      toast.error('Erro ao actualizar apontamentos: ' + error.message);
      setViewInitialDetailsEditor((prev) => ({ ...prev, saving: false }));
    }
  };

  const openProjectSaleConversionDialog = async (project) => {
    if (!project?.id) return;

    try {
      setConversionLoadingKey(project.id);
      setConversionDialog({
        open: true,
        loading: true,
        submitting: false,
        project,
        location: null,
        items: [],
        warnings: [],
        suggestedNotes: '',
        additionalNotes: '',
        vatOptions: [],
      });
      const preview = await api.get(`/api/projects/${project.id}/convert-to-sale-preview`);
      setConversionDialog({
        open: true,
        loading: false,
        submitting: false,
        project,
        location: preview.location || null,
        items: preview.items || [],
        warnings: preview.warnings || [],
        suggestedNotes: preview.suggested_notes || '',
        additionalNotes: '',
        vatOptions: preview.vat_regime_options || [],
      });
    } catch (error) {
      setConversionDialog((prev) => ({ ...prev, open: false, loading: false }));
      toast.error('Erro ao preparar conversão em faturação: ' + error.message);
    } finally {
      setConversionLoadingKey(null);
    }
  };

  const updateConversionItemField = (index, field, value) => {
    setConversionDialog((prev) => ({
      ...prev,
      items: prev.items.map((item, itemIndex) => (itemIndex === index ? { ...item, [field]: value } : item)),
    }));
  };

  const handleConfirmProjectConversion = async () => {
    const project = conversionDialog.project;
    if (!project?.id) return;

    try {
      setConversionDialog((prev) => ({ ...prev, submitting: true }));
      const result = await api.post(`/api/projects/${project.id}/convert-to-sale`, {
        sale_location_id: conversionDialog.location?.id || project.store_id || null,
        sale_location_name: conversionDialog.location?.name || project.store_name || null,
        additional_notes: conversionDialog.additionalNotes,
        items: conversionDialog.items,
      });

      toast.success(result.message || 'Obra convertida em venda rascunho');
      setConversionDialog({
        open: false,
        loading: false,
        submitting: false,
        project: null,
        location: null,
        items: [],
        warnings: [],
        suggestedNotes: '',
        additionalNotes: '',
        vatOptions: [],
      });
      await loadProjects();
      if (viewDialog.project?.id === project.id) {
        await openViewProjectDialog(project.id);
      }
      if (result.sale_id) {
        window.location.href = `/sales?sale_id=${encodeURIComponent(result.sale_id)}&from_project_id=${encodeURIComponent(project.id)}`;
      }
    } catch (error) {
      toast.error('Erro ao converter obra em faturação: ' + error.message);
      setConversionDialog((prev) => ({ ...prev, submitting: false }));
    }
  };

  const openDuplicateServiceDialog = async (project) => {
    if (!project?.id) return;

    try {
      setConversionLoadingKey(project.id);
      setDuplicateServiceDialog({
        open: true,
        loading: true,
        submitting: false,
        project,
        location: null,
        item: null,
        suggestedReason: '',
        revisitReason: '',
        additionalNotes: '',
        interventionDate: '',
        startTime: '',
        endTime: '',
        technicianName: '',
        vatOptions: [],
      });
      const preview = await api.get(`/api/projects/${project.id}/duplicate-service-preview`);
      setDuplicateServiceDialog({
        open: true,
        loading: false,
        submitting: false,
        project,
        location: preview.location || null,
        item: preview.item || null,
        suggestedReason: preview.suggested_reason || '',
        revisitReason: preview.suggested_reason || '',
        additionalNotes: '',
        interventionDate: preview.suggested_intervention_date || '',
        startTime: preview.suggested_start_time || '',
        endTime: preview.suggested_end_time || '',
        technicianName: preview.suggested_technician_name || '',
        vatOptions: preview.vat_regime_options || [],
      });
    } catch (error) {
      setDuplicateServiceDialog((prev) => ({ ...prev, open: false, loading: false }));
      toast.error('Erro ao preparar serviço complementar: ' + error.message);
    } finally {
      setConversionLoadingKey(null);
    }
  };

  const updateDuplicateServiceField = (field, value) => {
    setDuplicateServiceDialog((prev) => ({
      ...prev,
      item: { ...(prev.item || {}), [field]: value },
    }));
  };

  const handleCreateDuplicateServiceSale = async () => {
    const project = duplicateServiceDialog.project;
    if (!project?.id || !duplicateServiceDialog.item) return;

    try {
      setDuplicateServiceDialog((prev) => ({ ...prev, submitting: true }));
      const result = await api.post(`/api/projects/${project.id}/duplicate-service-to-sale`, {
        sale_location_id: duplicateServiceDialog.location?.id || project.store_id || null,
        sale_location_name: duplicateServiceDialog.location?.name || project.store_name || null,
        revisit_reason: duplicateServiceDialog.revisitReason,
        additional_notes: duplicateServiceDialog.additionalNotes,
        intervention_date: duplicateServiceDialog.interventionDate,
        start_time: duplicateServiceDialog.startTime,
        end_time: duplicateServiceDialog.endTime,
        technician_name: duplicateServiceDialog.technicianName,
        item: duplicateServiceDialog.item,
      });

      toast.success(result.message || 'Serviço complementar criado em nova venda draft');
      setDuplicateServiceDialog({
        open: false,
        loading: false,
        submitting: false,
        project: null,
        location: null,
        item: null,
        suggestedReason: '',
        revisitReason: '',
        additionalNotes: '',
        interventionDate: '',
        startTime: '',
        endTime: '',
        technicianName: '',
        vatOptions: [],
      });
      await loadProjects();
      if (viewDialog.project?.id === project.id) {
        await openViewProjectDialog(project.id);
      }
      if (result.sale_id) {
        window.location.href = `/sales?sale_id=${encodeURIComponent(result.sale_id)}&from_project_id=${encodeURIComponent(project.id)}`;
      }
    } catch (error) {
      toast.error('Erro ao criar venda complementar: ' + error.message);
      setDuplicateServiceDialog((prev) => ({ ...prev, submitting: false }));
    }
  };

  const openSpecificSale = (saleId, projectId) => {
    if (!saleId) return;
    window.location.href = `/sales?sale_id=${encodeURIComponent(saleId)}&from_project_id=${encodeURIComponent(projectId || '')}`;
  };

  const openLinkedSale = (project) => {
    if (!project?.linked_sale_id) {
      toast.error('Esta obra ainda não possui venda associada');
      return;
    }
    openSpecificSale(project.linked_sale_id, project.id);
  };

  const openCreateMaterialDialog = (project) => {
    if (!project?.id) return;
    setMaterialDialog({
      open: true,
      mode: 'create',
      project,
      saving: false,
      form: {
        ...EMPTY_MATERIAL_FORM,
        description: project.title ? `Complemento - ${project.title}` : '',
      },
    });
  };

  const openEditMaterialDialog = (project, material) => {
    if (!project?.id || !material) return;
    setMaterialDialog({
      open: true,
      mode: 'edit',
      project,
      saving: false,
      form: {
        ...EMPTY_MATERIAL_FORM,
        ...material,
        quantity: Number(material.quantity || 1),
        unit: material.unit || 'un',
      },
    });
  };

  const handleSaveMaterial = async () => {
    const project = materialDialog.project;
    if (!project?.id) return;

    const payload = {
      product_id: materialDialog.form.product_id || null,
      item_id: materialDialog.form.item_id || null,
      description: materialDialog.form.description?.trim() || '',
      quantity: Number(materialDialog.form.quantity || 1),
      unit: materialDialog.form.unit?.trim() || 'un',
      serial_number: materialDialog.form.serial_number?.trim() || null,
      notes: materialDialog.form.notes?.trim() || null,
    };

    if (!payload.description) {
      toast.error('Descrição do complemento é obrigatória');
      return;
    }

    if (payload.quantity <= 0) {
      toast.error('Quantidade inválida');
      return;
    }

    try {
      setMaterialDialog((prev) => ({ ...prev, saving: true }));
      if (materialDialog.mode === 'edit' && materialDialog.form.id) {
        await api.put(`/api/projects/${project.id}/materials/${materialDialog.form.id}`, payload);
        toast.success('Complemento actualizado com sucesso');
      } else {
        await api.post(`/api/projects/${project.id}/materials`, payload);
        toast.success('Complemento adicionado com sucesso');
      }

      setMaterialDialog({
        open: false,
        mode: 'create',
        project: null,
        saving: false,
        form: EMPTY_MATERIAL_FORM,
      });
      await loadProjects();
      await openViewProjectDialog(project.id);
    } catch (error) {
      toast.error('Erro ao guardar complemento: ' + error.message);
      setMaterialDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleConfirmDeleteMaterial = async () => {
    const project = deleteMaterialDialog.project;
    const material = deleteMaterialDialog.material;
    if (!project?.id || !material?.id) return;

    try {
      setDeleteMaterialDialog((prev) => ({ ...prev, deleting: true }));
      await api.delete(`/api/projects/${project.id}/materials/${material.id}`);
      toast.success('Complemento removido com sucesso');
      setDeleteMaterialDialog({ open: false, project: null, material: null, deleting: false });
      await loadProjects();
      await openViewProjectDialog(project.id);
    } catch (error) {
      toast.error('Erro ao remover complemento: ' + error.message);
      setDeleteMaterialDialog((prev) => ({ ...prev, deleting: false }));
    }
  };

  const openEditHistoryEntryDialog = (project, entry) => {
    if (!project?.id || !entry?.id) return;
    setHistoryEntryDialog({
      open: true,
      project,
      entry,
      saving: false,
      form: {
        display_title: entry.display_title || getProjectHistoryTitle(entry),
        admin_notes: entry.admin_notes || '',
        changed_by_name: entry.changed_by_name || '',
        changed_at: entry.changed_at || '',
        changed_fields_text: (entry.changed_fields || []).join(', '),
      },
    });
  };

  const handleSaveHistoryEntry = async () => {
    const project = historyEntryDialog.project;
    const entry = historyEntryDialog.entry;
    if (!project?.id || !entry?.id) return;

    try {
      setHistoryEntryDialog((prev) => ({ ...prev, saving: true }));
      await api.put(`/api/projects/${project.id}/initial-details-history/${entry.id}`, {
        display_title: historyEntryDialog.form.display_title?.trim() || null,
        admin_notes: historyEntryDialog.form.admin_notes?.trim() || null,
        changed_by_name: historyEntryDialog.form.changed_by_name?.trim() || null,
        changed_at: historyEntryDialog.form.changed_at || null,
        changed_fields: historyEntryDialog.form.changed_fields_text
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      });
      toast.success('Histórico visível actualizado com sucesso');
      setHistoryEntryDialog({
        open: false,
        project: null,
        entry: null,
        saving: false,
        form: { display_title: '', admin_notes: '', changed_by_name: '', changed_at: '', changed_fields_text: '' },
      });
      await openViewProjectDialog(project.id);
    } catch (error) {
      toast.error('Erro ao actualizar histórico: ' + error.message);
      setHistoryEntryDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDeleteHistoryEntry = async () => {
    const project = deleteHistoryEntryDialog.project;
    const entry = deleteHistoryEntryDialog.entry;
    if (!project?.id || !entry?.id) return;

    try {
      setDeleteHistoryEntryDialog((prev) => ({ ...prev, deleting: true }));
      await api.delete(`/api/projects/${project.id}/initial-details-history/${entry.id}`);
      toast.success('Entrada do histórico removida com sucesso');
      setDeleteHistoryEntryDialog({ open: false, project: null, entry: null, deleting: false });
      await openViewProjectDialog(project.id);
    } catch (error) {
      toast.error('Erro ao remover histórico: ' + error.message);
      setDeleteHistoryEntryDialog((prev) => ({ ...prev, deleting: false }));
    }
  };

  const openEditInterventionDialog = (project, entry) => {
    if (!project?.id || !entry?.id) return;
    setInterventionDialog({
      open: true,
      project,
      entry,
      saving: false,
      form: {
        description: entry.description || '',
        reason: entry.reason || '',
        notes: entry.notes || '',
        intervention_date: entry.intervention_date || '',
        start_time: entry.start_time || '',
        end_time: entry.end_time || '',
        technician_name: entry.technician_name || '',
      },
    });
  };

  const handleSaveIntervention = async () => {
    const project = interventionDialog.project;
    const entry = interventionDialog.entry;
    if (!project?.id || !entry?.id) return;

    try {
      setInterventionDialog((prev) => ({ ...prev, saving: true }));
      await api.put(`/api/projects/${project.id}/additional-interventions/${entry.id}`, {
        description: interventionDialog.form.description?.trim() || null,
        reason: interventionDialog.form.reason?.trim() || null,
        notes: interventionDialog.form.notes?.trim() || null,
        intervention_date: interventionDialog.form.intervention_date || null,
        start_time: interventionDialog.form.start_time || null,
        end_time: interventionDialog.form.end_time || null,
        technician_name: interventionDialog.form.technician_name?.trim() || null,
      });
      toast.success('Intervenção complementar actualizada com sucesso');
      setInterventionDialog({
        open: false,
        project: null,
        entry: null,
        saving: false,
        form: { description: '', reason: '', notes: '', intervention_date: '', start_time: '', end_time: '', technician_name: '' },
      });
      await openViewProjectDialog(project.id);
    } catch (error) {
      toast.error('Erro ao actualizar intervenção: ' + error.message);
      setInterventionDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDeleteIntervention = async () => {
    const project = deleteInterventionDialog.project;
    const entry = deleteInterventionDialog.entry;
    if (!project?.id || !entry?.id) return;

    try {
      setDeleteInterventionDialog((prev) => ({ ...prev, deleting: true }));
      await api.delete(`/api/projects/${project.id}/additional-interventions/${entry.id}`);
      toast.success('Intervenção complementar removida com sucesso');
      setDeleteInterventionDialog({ open: false, project: null, entry: null, deleting: false });
      await openViewProjectDialog(project.id);
    } catch (error) {
      toast.error('Erro ao remover intervenção: ' + error.message);
      setDeleteInterventionDialog((prev) => ({ ...prev, deleting: false }));
    }
  };

  const openDeleteProjectDialog = (project) => {
    if (!project?.id || !canDeleteProjects) return;
    setDeleteProjectDialog({ open: true, project, deleting: false });
  };

  const handleConfirmDeleteProject = async () => {
    const project = deleteProjectDialog.project;
    if (!project?.id) return;

    try {
      setDeleteProjectDialog((prev) => ({ ...prev, deleting: true }));
      await api.delete(`/api/projects/${project.id}`);
      if (viewDialog.project?.id === project.id) {
        closeViewProjectDialog();
      }
      toast.success('Obra apagada com sucesso');
      setDeleteProjectDialog({ open: false, project: null, deleting: false });
      await loadProjects();
    } catch (error) {
      toast.error('Erro ao apagar obra: ' + error.message);
      setDeleteProjectDialog((prev) => ({ ...prev, deleting: false }));
    }
  };

  const handleChecklistToggle = async (projectId, itemId, isCompleted) => {
    try {
      await api.put(`/api/projects/${projectId}/checklist/${itemId}?is_completed=${isCompleted}`);
      // Reload project details
      const updated = await api.get(`/api/projects/${projectId}`);
      setChecklistDialog({ open: true, project: updated });
      await refreshProjectsData();
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const getLatestSignatureByRole = (project, role) => {
    if (!project?.signatures?.length) return null;
    const filtered = project.signatures.filter((signature) => signature.signer_role === role);
    return filtered.length ? filtered[filtered.length - 1] : null;
  };

  const getLatestSignatureByRoles = (project, roles) => {
    if (!project?.signatures?.length) return null;
    const filtered = project.signatures.filter((signature) => roles.includes(signature.signer_role));
    return filtered.length ? filtered[filtered.length - 1] : null;
  };

  const openSignatureDialog = (project, role = 'client') => {
    const existingSignature = getLatestSignatureByRole(project, role);
    const defaultName = role === 'client' ? (project?.customer_name || '') : '';
    setSignatureSignerName(existingSignature?.signer_name || defaultName);
    setSignatureDialog({ open: true, project, role });
  };

  const handleSignature = async () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      toast.error('Por favor, assine antes de guardar');
      return;
    }

    const signatureData = signaturePadRef.current.toDataURL('image/png');
    const signerName = signatureSignerName;
    
    if (!signerName?.trim()) {
      toast.error('Nome do signatário é obrigatório');
      return;
    }

    try {
      const result = await api.post(`/api/projects/${signatureDialog.project.id}/sign`, {
        signer_name: signerName.trim(),
        signer_role: signatureDialog.role,
        signature_data: signatureData
      });
      
      toast.success(result.replaced_previous ? 'Assinatura actualizada com sucesso!' : 'Assinatura registada com sucesso!');
      
      // Close dialog and refresh
      setSignatureDialog({ open: false, project: null, role: 'client' });
      setSignatureSignerName('');
      await refreshProjectsData();
      
      // If can print now, show message
      if (result.can_print) {
        toast.info('Documento pronto para impressão');
      }
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleClearSignatureSlot = async (project, signatureSlot) => {
    if (!project?.id) return;

    const labels = {
      client: 'assinatura do cliente',
      technician: 'assinatura técnica',
    };

    if (!window.confirm(`Tem a certeza que deseja limpar a ${labels[signatureSlot] || 'assinatura'} desta obra?`)) {
      return;
    }

    try {
      const result = await api.post(`/api/projects/${project.id}/signatures/${signatureSlot}/clear`, {
        reason: `Limpeza manual da ${labels[signatureSlot] || 'assinatura'} via Obras/Projectos`,
      });
      toast.success(result.message || 'Assinatura removida com sucesso');
      loadProjects();
      if (viewDialog.project?.id === project.id) {
        await openViewProjectDialog(project.id);
      }
    } catch (error) {
      toast.error('Erro ao limpar assinatura: ' + error.message);
    }
  };

  const handleClearAllProjectSignatures = async (project) => {
    if (!project?.id) return;

    if (!window.confirm('Tem a certeza que deseja limpar TODAS as assinaturas desta obra?')) {
      return;
    }

    try {
      const result = await api.post(`/api/projects/${project.id}/signatures/clear-all`, {
        reason: 'Limpeza manual de todas as assinaturas via Obras/Projectos',
      });
      toast.success(result.message || 'Todas as assinaturas foram removidas');
      loadProjects();
      if (viewDialog.project?.id === project.id) {
        await openViewProjectDialog(project.id);
      }
    } catch (error) {
      toast.error('Erro ao limpar assinaturas: ' + error.message);
    }
  };

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const handlePrintCheck = async (project) => {
    try {
      const result = await api.get(`/api/projects/${project.id}/can-print`);
      setPrintDialog({
        open: true,
        project: project,
        canPrint: result.can_print,
        reason: result.reason,
        allowPendingPrint: result.allow_pending_print,
        executionComplete: result.execution_complete,
        missingFields: result.missing_fields || []
      });
    } catch (error) {
      toast.error('Erro ao verificar permissão de impressão');
    }
  };

  // Abrir dialog de detalhes de execução
  const openExecutionDialog = (project) => {
    const exec = project.execution_details || {};
    setExecutionForm({
      execution_date: exec.execution_date || new Date().toISOString().split('T')[0],
      execution_end_date: exec.execution_end_date || exec.execution_date || new Date().toISOString().split('T')[0],
      start_time: exec.start_time || '',
      end_time: exec.end_time || '',
      work_order: exec.work_order || '',
      technicians_names: exec.technicians_names?.length > 0 ? exec.technicians_names : [''],
      store_id: exec.store_id || project.store_id || '',
      execution_location: exec.execution_location || project.work_location || '',
      final_observations: exec.final_observations || '',
      work_segments: exec.work_segments?.length ? exec.work_segments : [{ work_date: exec.execution_date || new Date().toISOString().split('T')[0], start_time: exec.start_time || '', end_time: exec.end_time || '' }]
    });
    setExecutionDialog({ open: true, project });
  };

  // Guardar detalhes de execução
  const handleSaveExecution = async () => {
    const { execution_date, execution_end_date, start_time, end_time, execution_location, technicians_names, store_id } = executionForm;
    
    if (!execution_date || !start_time || !end_time) {
      toast.error('Data e horas são obrigatórias');
      return;
    }
    if (!execution_location) {
      toast.error('Local de execução é obrigatório');
      return;
    }
    const validTechs = technicians_names.filter(t => t.trim());
    if (validTechs.length === 0) {
      toast.error('Pelo menos um técnico é obrigatório');
      return;
    }

    try {
      await api.post(`/api/projects/${executionDialog.project.id}/execution`, {
        execution_date,
        execution_end_date,
        start_time,
        end_time,
        execution_location,
        technicians_names: validTechs,
        store_id: store_id || null,
        work_order: executionForm.work_order || null,
        final_observations: executionForm.final_observations || null,
        work_segments: (executionForm.work_segments || []).filter((segment) => segment.work_date && segment.start_time && segment.end_time)
      });

      toast.success('Detalhes de execução guardados!');
      setExecutionDialog({ open: false, project: null });
      loadProjects();
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  // Abrir dialog de campos específicos por tipo
  const openTypeFieldsDialog = (project) => {
    const type = project.project_type;
    let fields = {};
    
    if (type === 'gps_fleet') fields = { ...DEFAULT_GPS_FIELDS, ...(project.gps_fields || {}) };
    else if (type === 'cctv') fields = project.cctv_fields || { cameras_count: 0, dvr_nvr_type: '', dvr_nvr_model: '', storage_capacity: '', ip_config: '', network_config: '', recording_test: 'pending', remote_access_configured: false, notes: '' };
    else if (type === 'fuel_management') fields = project.fuel_fields || { sensor_type: '', sensor_model: '', tank_capacity: 0, calibration_done: false, calibration_points: 0, fuel_type: '', notes: '' };
    else if (type === 'access_control') fields = project.access_fields || { readers_count: 0, reader_type: '', controller_model: '', doors_count: 0, users_registered: 0, software_installed: false, notes: '' };
    else if (type === 'networking') fields = project.network_fields || { network_type: '', cable_meters: 0, cable_type: '', active_equipment: [], equipment_count: 0, connectivity_test: 'pending', speed_test_result: '', notes: '' };
    else fields = project.other_fields || { custom_fields: {}, notes: '' };
    
    setTypeFields(fields);
    setApplyChecklistTemplate(false);
    setTypeFieldsDialog({ open: true, project });
  };

  // Guardar campos específicos por tipo
  const handleSaveTypeFields = async () => {
    if (typeFieldsDialog.project?.project_type === 'gps_fleet' && (typeFields.operation_type || 'installation') === 'other' && !typeFields.operation_other_description?.trim()) {
      toast.error('Descrição manual é obrigatória quando o Tipo de Operação é Outro');
      return;
    }
    try {
      const payload = {
        ...typeFields,
        apply_checklist_template: applyChecklistTemplate,
      };
      const result = await api.put(`/api/projects/${typeFieldsDialog.project.id}/type-fields`, payload);
      if (result?.checklist_sync?.message) {
        toast.info(result.checklist_sync.message);
      }
      toast.success('Campos específicos guardados!');
      setTypeFieldsDialog({ open: false, project: null });
      loadProjects();
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  // Adicionar/remover técnico na lista
  const addTechnician = () => {
    setExecutionForm(prev => ({ ...prev, technicians_names: [...prev.technicians_names, ''] }));
  };
  const removeTechnician = (index) => {
    setExecutionForm(prev => ({
      ...prev,
      technicians_names: prev.technicians_names.filter((_, i) => i !== index)
    }));
  };
  const updateTechnician = (index, value) => {
    setExecutionForm(prev => {
      const names = [...prev.technicians_names];
      names[index] = value;
      return { ...prev, technicians_names: names };
    });
  };

  const addWorkSegment = () => {
    setExecutionForm(prev => ({
      ...prev,
      work_segments: [...(prev.work_segments || []), { work_date: prev.execution_end_date || prev.execution_date || '', start_time: '', end_time: '' }]
    }));
  };

  const removeWorkSegment = (index) => {
    setExecutionForm(prev => ({
      ...prev,
      work_segments: prev.work_segments.filter((_, i) => i !== index)
    }));
  };

  const updateWorkSegment = (index, field, value) => {
    setExecutionForm(prev => ({
      ...prev,
      work_segments: prev.work_segments.map((segment, i) => i === index ? { ...segment, [field]: value } : segment)
    }));
  };

  const handlePrint = async (isPending = false) => {
    const project = printDialog.project;
    if (!project) return;

    try {
      const token = getStoredAuthToken();
      // Generate PDF via API (to include signature)
      const response = await fetch(`${API_URL}/api/projects/${project.id}/pdf${isPending ? '?pending=true' : ''}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erro ao gerar PDF');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // Open in new tab for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => {
          printWindow.print();
        });
      }
      
      setPrintDialog({ open: false, project: null, canPrint: false });
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleUpdateProjectStore = async () => {
    if (!storeDialog.project?.id || !storeDialog.storeId) {
      toast.error('Seleccione uma loja válida');
      return;
    }
    try {
      await api.put(`/api/projects/${storeDialog.project.id}/store`, { store_id: storeDialog.storeId });
      toast.success('Loja da obra actualizada');
      setStoreDialog({ open: false, project: null, storeId: '' });
      loadProjects();
      if (viewDialog.project?.id === storeDialog.project.id) {
        const updated = await api.get(`/api/projects/${storeDialog.project.id}`);
        setViewDialog({ open: true, project: updated });
      }
    } catch (error) {
      toast.error('Erro: ' + error.message);
    }
  };

  const handleGeneratePublicLink = async () => {
    if (!portalDialog.project?.id) return;
    const allowedEmails = portalDialog.allowedEmailsRaw
      .split(/[\n,;]+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (!allowedEmails.length) {
      toast.error('Indique pelo menos um email permitido');
      return;
    }

    try {
      setPortalDialog((prev) => ({ ...prev, creating: true }));
      const result = await api.post(`/api/projects/${portalDialog.project.id}/public-link`, {
        allowed_emails: allowedEmails,
        expires_in_days: Number(portalDialog.expiresInDays || 7),
      });

      const absoluteLink = result.path ? `${window.location.origin}${result.path}` : result.url;
      setPortalDialog((prev) => ({
        ...prev,
        generatedLink: absoluteLink,
        generatedPath: result.path,
        expiresAt: result.expires_at,
      }));
      toast.success('Link de cliente gerado com sucesso');
    } catch (error) {
      toast.error('Erro: ' + error.message);
    } finally {
      setPortalDialog((prev) => ({ ...prev, creating: false }));
    }
  };

  const handleCopyPortalLink = async () => {
    if (!portalDialog.generatedLink) return;
    try {
      await navigator.clipboard.writeText(portalDialog.generatedLink);
      toast.success('Link copiado para a área de transferência');
    } catch {
      toast.error('Não foi possível copiar o link');
    }
  };

  const handleRunIntegrityCheck = async (project) => {
    if (!project?.id) return;
    setIntegrityDialog({ open: true, loading: true, project, report: null });
    try {
      const result = await api.get(`/api/projects/${project.id}/integrity-check`);
      setIntegrityDialog({ open: true, loading: false, project, report: result });
    } catch (error) {
      setIntegrityDialog({ open: true, loading: false, project, report: null });
      toast.error('Erro ao executar check de conformidade: ' + error.message);
    }
  };

  const selectClient = (client) => {
    setForm({
      ...form,
      customer_id: client.id,
      customer_name: client.name,
      customer_phone: client.phone || '',
      customer_email: client.email || ''
    });
    setClientSearch(client.name);
  };

  const updateCreateGpsField = (field, value) => {
    setForm((prev) => ({
      ...prev,
      gps_fields: {
        ...prev.gps_fields,
        [field]: value,
      },
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: pt });
    } catch {
      return dateStr;
    }
  };

  const formatMoney = (value) => new Intl.NumberFormat('pt-MZ', {
    style: 'currency',
    currency: 'MZN',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

  const getProjectHistoryTitle = (entry) => {
    if (entry?.display_title?.trim()) return entry.display_title;
    const changeType = entry?.change_type;
    if (changeType === 'project_converted_to_sale') return 'Conversão em faturação/venda';
    if (changeType === 'project_material_added') return 'Complemento adicionado';
    if (changeType === 'project_material_updated') return 'Complemento actualizado';
    if (changeType === 'project_material_deleted') return 'Complemento removido';
    if (changeType === 'project_deleted') return 'Obra apagada';
    if (changeType === 'additional_service_intervention_updated') return 'Intervenção complementar actualizada';
    if (changeType === 'additional_service_intervention_deleted') return 'Intervenção complementar removida';
    return 'Actualização dos dados iniciais';
  };

  const selectedMaterialProduct = useMemo(
    () => productCatalog.find((product) => product.id === materialDialog.form.product_id) || null,
    [materialDialog.form.product_id, productCatalog]
  );

  const getConversionLineNetTotal = (item) => {
    const quantity = Math.max(Number(item.quantity || 0), 0);
    const unitPrice = Math.max(Number(item.unit_price || 0), 0);
    const discount = Math.max(Number(item.discount || 0), 0);
    const discountPercent = Math.max(Math.min(Number(item.discount_percent || 0), 100), 0);
    const gross = quantity * unitPrice;
    const afterPercent = gross - ((gross * discountPercent) / 100);
    return Math.max(afterPercent - discount, 0);
  };

  const getConversionLineGrossTotal = (item) => {
    const net = getConversionLineNetTotal(item);
    const vatRate = Math.max(Number(item.vat_rate || 0), 0);
    if (item.vat_regime === 'exclusive') {
      return net + ((net * vatRate) / 100);
    }
    return net;
  };

  const conversionSummary = conversionDialog.items.reduce((summary, item) => {
    summary.net += getConversionLineNetTotal(item);
    summary.gross += getConversionLineGrossTotal(item);
    return summary;
  }, { net: 0, gross: 0 });

  const getChecklistProgress = (project) => {
    const items = project.checklist_items || [];
    if (items.length === 0) return { completed: 0, total: 0, percent: 0 };
    const completed = items.filter(i => i.is_completed).length;
    return {
      completed,
      total: items.length,
      percent: Math.round(completed / items.length * 100)
    };
  };

  const filteredProjects = useMemo(() => projects.filter(p =>
    p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.project_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  ), [projects, searchTerm]);

  const filteredClients = useMemo(() => clients.filter(c =>
    c.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    c.code?.toLowerCase().includes(clientSearch.toLowerCase())
  ).slice(0, 10), [clientSearch, clients]);

  return (
    <div className="p-6 space-y-6" data-testid="obras-projectos-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <HardHat className="h-7 w-7 text-amber-500" />
            Obras / Projectos Técnicos
          </h1>
          <p className="text-muted-foreground">GPS/Frota, Combustíveis, CCTV, Redes</p>
        </div>
        <Button onClick={() => { resetForm(); setCreateDialog(true); }} data-testid="create-project-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nova Obra
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {Object.entries(PROJECT_STATUS).slice(0, 5).map(([key, status]) => {
          const count = projects.filter(p => p.status === key).length;
          return (
            <Card key={key} className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setFilterStatus(key)}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{status.label}</p>
                    <p className="text-2xl font-bold">{count}</p>
                  </div>
                  <Badge className={status.color}>{count}</Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar por título, número ou cliente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
            data-testid="search-projects-input"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos Estados</SelectItem>
            {Object.entries(PROJECT_STATUS).map(([key, status]) => (
              <SelectItem key={key} value={key}>{status.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos Tipos</SelectItem>
            {Object.entries(PROJECT_TYPES).map(([key, type]) => (
              <SelectItem key={key} value={key}>{type.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={loadProjects}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Projects List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Projectos ({filteredProjects.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin" />
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <HardHat className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum projecto encontrado</p>
              <Button variant="outline" className="mt-4" onClick={() => setCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" /> Criar Primeira Obra
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredProjects.map((project) => {
                const typeInfo = PROJECT_TYPES[project.project_type] || PROJECT_TYPES.other;
                const statusInfo = PROJECT_STATUS[project.status] || PROJECT_STATUS.draft;
                const progress = getChecklistProgress(project);
                const TypeIcon = typeInfo.icon;
                const hasTechnicalSignature = Boolean(getLatestSignatureByRoles(project, ['technician', 'lead', 'supervisor']));
                const hasAnySignature = Boolean((project.signatures || []).length);

                return (
                  <Card key={project.id} className="hover:shadow-md transition-shadow" data-testid={`project-row-${project.id}`}>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-lg ${typeInfo.color}`}>
                            <TypeIcon className="h-6 w-6" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">
                                {project.project_number}
                              </span>
                              <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                              {project.is_signed_by_client ? (
                                <Badge className="bg-green-100 text-green-800">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Assinado
                                </Badge>
                              ) : (
                                <Badge className="bg-orange-100 text-orange-800">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Sem Assinatura
                                </Badge>
                              )}
                              {project.billing_status === 'invoiced' && (
                                <Badge className="bg-emerald-100 text-emerald-800" data-testid={`project-billing-badge-${project.id}`}>
                                  Faturada
                                </Badge>
                              )}
                            </div>
                            <h3 className="font-semibold">{project.title}</h3>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1 flex-wrap">
                              <span className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                {project.customer_name}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {project.work_location}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(project.created_at)}
                              </span>
                              <span className="flex items-center gap-1" data-testid={`project-store-${project.id}`}>
                                <Store className="h-3 w-3" />
                                {project.store_name || 'Loja não definida'}
                              </span>
                            </div>
                            {progress.total > 0 && (
                              <div className="flex items-center gap-2 mt-2">
                                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-green-500 transition-all"
                                    style={{ width: `${progress.percent}%` }}
                                  />
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {progress.completed}/{progress.total} ({progress.percent}%)
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-2" data-testid={`project-row-actions-${project.id}`}>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openExecutionDialog(project)}
                            data-testid={`execution-${project.id}`}
                            title="Detalhes de Execução"
                            className={`${inventoryStyleActionButtonClass} ${project.execution_details ? 'border-green-300 text-green-700 hover:bg-green-50' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}
                          >
                            <Timer className="mr-2 h-4 w-4" /> Execução
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openTypeFieldsDialog(project)}
                            data-testid={`type-fields-${project.id}`}
                            title="Campos Específicos"
                            className={inventoryStyleActionButtonClass}
                          >
                            <Settings className="mr-2 h-4 w-4" /> Campos
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setChecklistDialog({ open: true, project })}
                            data-testid={`checklist-${project.id}`}
                            title="Checklist"
                            className={inventoryStyleActionButtonClass}
                          >
                            <ClipboardList className="mr-2 h-4 w-4" /> Checklist
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openViewProjectDialog(project)}
                            data-testid={`view-project-${project.id}`}
                            title="Ver Detalhes"
                            className={inventoryStyleActionButtonClass}
                          >
                            <Eye className="mr-2 h-4 w-4" /> Detalhes
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => project.linked_sale_id ? openLinkedSale(project) : openProjectSaleConversionDialog(project)}
                            disabled={conversionLoadingKey === project.id}
                            data-testid={`convert-project-to-sale-${project.id}`}
                            title={project.linked_sale_id ? 'Abrir venda associada' : 'Converter em venda/fatura'}
                            className={inventoryStyleActionButtonClass}
                          >
                            {conversionLoadingKey === project.id ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                            {project.linked_sale_id ? 'Abrir Venda' : 'Faturar'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openDuplicateServiceDialog(project)}
                            disabled={conversionLoadingKey === project.id}
                            data-testid={`duplicate-project-service-${project.id}`}
                            title="Duplicar serviço em nova venda complementar"
                            className={inventoryStyleActionButtonClass}
                          >
                            <Copy className="mr-2 h-4 w-4" /> Duplicar Serviço
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openSignatureDialog(project, 'client')}
                            className={`${inventoryStyleActionButtonClass} ${project.is_signed_by_client ? 'border-green-300 text-green-700 hover:bg-green-50' : 'border-orange-300 text-orange-700 hover:bg-orange-50'}`}
                            data-testid={`sign-${project.id}`}
                            title={project.is_signed_by_client ? 'Editar assinatura do cliente' : 'Assinar'}
                          >
                            <Pen className="mr-2 h-4 w-4" /> Assinatura Cliente
                          </Button>
                          {canClearProjectSignatures && project.is_signed_by_client && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleClearSignatureSlot(project, 'client')}
                              className={inventoryStyleDangerButtonClass}
                              data-testid={`clear-client-signature-${project.id}`}
                              title="Limpar assinatura do cliente"
                            >
                              <XCircle className="mr-2 h-4 w-4" /> Limpar Cliente
                            </Button>
                          )}
                          {canClearProjectSignatures && hasTechnicalSignature && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleClearSignatureSlot(project, 'technician')}
                              className={inventoryStyleDangerButtonClass}
                              data-testid={`clear-technician-signature-${project.id}`}
                              title="Limpar assinatura técnica"
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Limpar Técnica
                            </Button>
                          )}
                          {canClearProjectSignatures && hasAnySignature && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleClearAllProjectSignatures(project)}
                              className={inventoryStyleDangerButtonClass}
                              data-testid={`clear-all-signatures-${project.id}`}
                              title="Limpar todas as assinaturas"
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Limpar Todas
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePrintCheck(project)}
                            data-testid={`print-${project.id}`}
                            title="Imprimir"
                            className={inventoryStyleActionButtonClass}
                          >
                            <Printer className="mr-2 h-4 w-4" /> Imprimir
                          </Button>
                          {canDeleteProjects && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteProjectDialog(project)}
                              data-testid={`delete-project-${project.id}`}
                              title="Apagar obra"
                              className={inventoryStyleDangerButtonClass}
                            >
                              <Trash2 className="mr-2 h-4 w-4" /> Apagar
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* CREATE DIALOG */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HardHat className="h-5 w-5 text-amber-500" />
              Abertura de Obra / Projecto
            </DialogTitle>
            <DialogDescription>Serviço técnico de campo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Project Type */}
            <div>
              <Label>Tipo de Projecto *</Label>
              <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(PROJECT_TYPES).map(([key, type]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <type.icon className="h-4 w-4" />
                        {type.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title & Description */}
            <div>
              <Label>Título da Obra *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex: Instalação GPS Veículo ABC-123"
              />
            </div>
            <div>
              <Label>Descrição</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Descrição geral do projecto..."
                rows={2}
              />
            </div>

            {/* Client Selection */}
            <div className="border rounded-lg p-4 space-y-3">
              <Label className="text-base font-medium flex items-center gap-2">
                <User className="h-4 w-4" />
                Cliente *
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={clientSearch}
                  onChange={(e) => {
                    setClientSearch(e.target.value);
                    if (form.customer_id && e.target.value !== form.customer_name) {
                      setForm({ ...form, customer_id: '', customer_name: '', customer_phone: '', customer_email: '' });
                    }
                  }}
                  placeholder="Pesquisar cliente por nome ou código..."
                  className="pl-9"
                />
              </div>
              {clientSearch && !form.customer_id && filteredClients.length > 0 && (
                <div className="border rounded-md max-h-40 overflow-y-auto">
                  {filteredClients.map(client => (
                    <div
                      key={client.id}
                      className="p-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                      onClick={() => selectClient(client)}
                    >
                      <div>
                        <p className="font-medium">{client.name}</p>
                        <p className="text-xs text-muted-foreground">{client.code} • {client.phone || '-'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {form.customer_id && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Telefone</Label>
                    <Input value={form.customer_phone} readOnly className="bg-gray-50" />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input value={form.customer_email} readOnly className="bg-gray-50" />
                  </div>
                </div>
              )}
            </div>

            {/* Store Selection */}
            <div className="border rounded-lg p-4 space-y-3" data-testid="project-store-selection-card">
              <Label className="text-base font-medium flex items-center gap-2">
                <Store className="h-4 w-4" />
                Loja de Origem da Obra *
              </Label>
              <Select
                value={form.store_id || ''}
                onValueChange={(value) => setForm({ ...form, store_id: value })}
              >
                <SelectTrigger data-testid="project-store-select-trigger">
                  <SelectValue placeholder="Seleccione a loja" />
                </SelectTrigger>
                <SelectContent>
                  {availableStores.map((store) => (
                    <SelectItem key={store.id} value={store.id} data-testid={`project-store-select-option-${store.id}`}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Work Location */}
            <div className="border rounded-lg p-4 space-y-3">
              <Label className="text-base font-medium flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Local da Obra *
              </Label>
              <div>
                <Label className="text-xs">Nome do Local</Label>
                <Input
                  value={form.work_location}
                  onChange={(e) => setForm({ ...form, work_location: e.target.value })}
                  placeholder="Ex: Fábrica XYZ, Veículo ABC-123, Escritório Central"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Endereço</Label>
                  <Input
                    value={form.work_address}
                    onChange={(e) => setForm({ ...form, work_address: e.target.value })}
                    placeholder="Rua, número..."
                  />
                </div>
                <div>
                  <Label className="text-xs">Cidade</Label>
                  <Input
                    value={form.work_city}
                    onChange={(e) => setForm({ ...form, work_city: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Coordenadas GPS (opcional)</Label>
                <Input
                  value={form.gps_coordinates}
                  onChange={(e) => setForm({ ...form, gps_coordinates: e.target.value })}
                  placeholder="-25.9692, 32.5732"
                />
              </div>
            </div>

            {/* Technical Description */}
            <div>
              <Label>Descrição Técnica</Label>
              <Textarea
                value={form.technical_description}
                onChange={(e) => setForm({ ...form, technical_description: e.target.value })}
                placeholder="Detalhes técnicos, especificações, requisitos..."
                rows={3}
              />
            </div>

            {form.project_type === 'gps_fleet' && (
              <div className="border rounded-lg p-4 space-y-4" data-testid="gps-fleet-create-fields-card">
                <div>
                  <Label className="text-base font-medium flex items-center gap-2">
                    <Truck className="h-4 w-4" />
                    Campos Específicos — GPS / Gestão de Frota
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">Bloco específico apenas para GPS / Gestão de Frota, sem afectar os demais tipos.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Tipo de Operação</Label>
                    <Select value={form.gps_fields.operation_type} onValueChange={(v) => updateCreateGpsField('operation_type', v)}>
                      <SelectTrigger data-testid="gps-create-operation-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{GPS_OPERATION_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {GPS_INSTALLATION_OPERATION_TYPES.includes(form.gps_fields.operation_type) && (
                    <div>
                      <Label className="text-xs">Tipo de Instalação</Label>
                      <Select value={form.gps_fields.installation_type || 'new_installation'} onValueChange={(v) => updateCreateGpsField('installation_type', v)}>
                        <SelectTrigger data-testid="gps-create-installation-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{GPS_INSTALLATION_TYPE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {form.gps_fields.operation_type === 'uninstallation' && (
                    <div>
                      <Label className="text-xs">Campo de Desinstalação</Label>
                      <Select value={form.gps_fields.uninstallation_type || 'permanent_uninstallation'} onValueChange={(v) => updateCreateGpsField('uninstallation_type', v)}>
                        <SelectTrigger data-testid="gps-create-uninstallation-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{GPS_UNINSTALLATION_TYPE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {form.gps_fields.operation_type === 'other' && (
                  <div>
                    <Label className="text-xs">Descrição manual *</Label>
                    <Textarea value={form.gps_fields.operation_other_description || ''} onChange={(e) => updateCreateGpsField('operation_other_description', e.target.value)} rows={2} data-testid="gps-create-operation-other-description" />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div><Label className="text-xs">Matrícula</Label><Input value={form.gps_fields.vehicle_plate || ''} onChange={(e) => updateCreateGpsField('vehicle_plate', e.target.value)} data-testid="gps-create-vehicle-plate" /></div>
                  <div><Label className="text-xs">Marca</Label><Input value={form.gps_fields.vehicle_brand || ''} onChange={(e) => updateCreateGpsField('vehicle_brand', e.target.value)} /></div>
                  <div><Label className="text-xs">Modelo</Label><Input value={form.gps_fields.vehicle_model || ''} onChange={(e) => updateCreateGpsField('vehicle_model', e.target.value)} /></div>
                  <div><Label className="text-xs">Número da Frota</Label><Input value={form.gps_fields.fleet_number || ''} onChange={(e) => updateCreateGpsField('fleet_number', e.target.value)} /></div>
                  <div><Label className="text-xs">Tipo de Viatura</Label><Input value={form.gps_fields.vehicle_type || ''} onChange={(e) => updateCreateGpsField('vehicle_type', e.target.value)} /></div>
                  <div><Label className="text-xs">Centro Operacional</Label><Input value={form.gps_fields.operational_center || ''} onChange={(e) => updateCreateGpsField('operational_center', e.target.value)} /></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div><Label className="text-xs">Tipo de Equipamento GPS</Label><Input value={form.gps_fields.gps_equipment_type || ''} onChange={(e) => updateCreateGpsField('gps_equipment_type', e.target.value)} /></div>
                  <div><Label className="text-xs">Marca / Modelo do Equipamento</Label><Input value={form.gps_fields.equipment_brand_model || ''} onChange={(e) => updateCreateGpsField('equipment_brand_model', e.target.value)} /></div>
                  <div><Label className="text-xs">IMEI</Label><Input value={form.gps_fields.imei_gps || ''} onChange={(e) => updateCreateGpsField('imei_gps', e.target.value)} /></div>
                  <div><Label className="text-xs">Serial Number</Label><Input value={form.gps_fields.serial_number || ''} onChange={(e) => updateCreateGpsField('serial_number', e.target.value)} /></div>
                  <div><Label className="text-xs">SIM Number</Label><Input value={form.gps_fields.sim_card_number || ''} onChange={(e) => updateCreateGpsField('sim_card_number', e.target.value)} /></div>
                  <div><Label className="text-xs">Estado antes da intervenção</Label><Input value={form.gps_fields.equipment_condition_before || ''} onChange={(e) => updateCreateGpsField('equipment_condition_before', e.target.value)} /></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div><Label className="text-xs">Motivo da intervenção</Label><Input value={form.gps_fields.intervention_reason || ''} onChange={(e) => updateCreateGpsField('intervention_reason', e.target.value)} /></div>
                  <div><Label className="text-xs">Cliente final / Departamento</Label><Input value={form.gps_fields.final_customer_department || ''} onChange={(e) => updateCreateGpsField('final_customer_department', e.target.value)} /></div>
                  <div><Label className="text-xs">Local da intervenção</Label><Input value={form.gps_fields.intervention_site || ''} onChange={(e) => updateCreateGpsField('intervention_site', e.target.value)} /></div>
                  <div><Label className="text-xs">Data prevista</Label><Input type="date" value={form.gps_fields.planned_date || ''} onChange={(e) => updateCreateGpsField('planned_date', e.target.value)} /></div>
                  <div>
                    <Label className="text-xs">Prioridade</Label>
                    <Select value={form.gps_fields.priority || 'normal'} onValueChange={(v) => updateCreateGpsField('priority', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Baixa</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="critical">Crítica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Estado final do serviço</Label><Input value={form.gps_fields.final_service_status || ''} onChange={(e) => updateCreateGpsField('final_service_status', e.target.value)} /></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 rounded-md border p-3"><Checkbox checked={!!form.gps_fields.requires_travel} onCheckedChange={(v) => updateCreateGpsField('requires_travel', v === true)} /><span className="text-sm">Necessita deslocação?</span></div>
                  <div className="flex items-center gap-2 rounded-md border p-3"><Checkbox checked={!!form.gps_fields.requires_final_tests} onCheckedChange={(v) => updateCreateGpsField('requires_final_tests', v === true)} /><span className="text-sm">Necessita testes finais?</span></div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Observações técnicas</Label>
                    <Textarea value={form.gps_fields.technical_observations || ''} onChange={(e) => updateCreateGpsField('technical_observations', e.target.value)} rows={2} />
                  </div>
                  <div>
                    <Label className="text-xs">Peças / materiais usados</Label>
                    <Textarea value={form.gps_fields.materials_used || ''} onChange={(e) => updateCreateGpsField('materials_used', e.target.value)} rows={2} />
                  </div>
                </div>

                <div className="rounded-lg border bg-muted/20 p-3" data-testid="gps-create-questionnaire-preview">
                  <p className="text-sm font-medium mb-2">Questionário operacional dinâmico</p>
                  <ul className="space-y-1 text-xs text-muted-foreground list-disc pl-4">
                    {buildGpsQuestionnairePreview(form.gps_fields).slice(0, 10).map((item) => <li key={item}>{item}</li>)}
                  </ul>
                </div>
              </div>
            )}

            <div>
              <Label>Âmbito do Trabalho</Label>
              <Textarea
                value={form.scope_of_work}
                onChange={(e) => setForm({ ...form, scope_of_work: e.target.value })}
                placeholder="O que será feito nesta obra..."
                rows={2}
              />
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data Início Prevista</Label>
                <Input
                  type="date"
                  value={form.scheduled_start_date}
                  onChange={(e) => setForm({ ...form, scheduled_start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Data Fim Prevista</Label>
                <Input
                  type="date"
                  value={form.scheduled_end_date}
                  onChange={(e) => setForm({ ...form, scheduled_end_date: e.target.value })}
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>Notas Internas</Label>
              <Textarea
                value={form.internal_notes}
                onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}
                placeholder="Notas visíveis apenas internamente..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreate} data-testid="confirm-create-project-btn">
              <Plus className="h-4 w-4 mr-2" />
              Criar Obra
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VIEW DIALOG */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => {
        if (!open) {
          closeViewProjectDialog();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="project-details-dialog-description">
          <DialogHeader>
            <DialogTitle>Detalhes da Obra</DialogTitle>
            <DialogDescription id="project-details-dialog-description">
              Consulte os dados da obra, histórico visível, complementos, intervenções e acções operacionais.
            </DialogDescription>
          </DialogHeader>
          {viewDialog.project && (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <div className={`p-3 rounded-lg ${PROJECT_TYPES[viewDialog.project.project_type]?.color || ''}`}>
                  {(() => {
                    const TypeIcon = PROJECT_TYPES[viewDialog.project.project_type]?.icon || HardHat;
                    return <TypeIcon className="h-8 w-8" />;
                  })()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-mono bg-white px-2 py-0.5 rounded border">
                      {viewDialog.project.project_number}
                    </span>
                    <Badge className={PROJECT_STATUS[viewDialog.project.status]?.color}>
                      {PROJECT_STATUS[viewDialog.project.status]?.label}
                    </Badge>
                    {viewDialog.project.billing_status === 'invoiced' && (
                      <Badge className="bg-emerald-100 text-emerald-800">Faturada</Badge>
                    )}
                    {viewDialog.project.is_signed_by_client ? (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Assinado
                      </Badge>
                    ) : (
                      <Badge className="bg-orange-100 text-orange-800">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Pendente Assinatura
                      </Badge>
                    )}
                  </div>
                  <h3 className="text-xl font-bold">{viewDialog.project.title}</h3>
                  <p className="text-muted-foreground">{viewDialog.project.description}</p>
                </div>
              </div>

              {/* Client & Location Info */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Cliente
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">{viewDialog.project.customer_name}</p>
                    {viewDialog.project.customer_phone && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {viewDialog.project.customer_phone}
                      </p>
                    )}
                    {viewDialog.project.customer_email && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {viewDialog.project.customer_email}
                      </p>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Local da Obra
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="font-medium">{viewDialog.project.work_location}</p>
                    {viewDialog.project.work_address && (
                      <p className="text-sm text-muted-foreground">{viewDialog.project.work_address}</p>
                    )}
                    <p className="text-sm text-muted-foreground">{viewDialog.project.work_city}</p>
                    <p className="text-sm text-muted-foreground mt-1" data-testid="view-project-store-name">
                      Loja origem: {viewDialog.project.store_name || 'Não definida'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Initial Details */}
              <Card data-testid="project-initial-details-section">
                <CardHeader className="pb-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Dados iniciais da Obra
                      </CardTitle>
                      <CardDescription>
                        Visualize e edite os apontamentos preenchidos no arranque do projecto.
                      </CardDescription>
                    </div>
                    {!viewInitialDetailsEditor.editing ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setViewInitialDetailsEditor((prev) => ({ ...prev, editing: true }))}
                        data-testid="view-project-edit-initial-details-button"
                      >
                        <Edit2 className="h-4 w-4 mr-1" /> Editar dados iniciais
                      </Button>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelInitialDetailsEditing}
                          data-testid="view-project-cancel-initial-details-button"
                        >
                          <XCircle className="h-4 w-4 mr-1" /> Cancelar
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveInitialDetails}
                          disabled={viewInitialDetailsEditor.saving}
                          data-testid="view-project-save-initial-details-button"
                        >
                          <Save className="h-4 w-4 mr-1" /> {viewInitialDetailsEditor.saving ? 'A guardar...' : 'Guardar alterações'}
                        </Button>
                      </div>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">Descrição inicial</Label>
                    {viewInitialDetailsEditor.editing ? (
                      <Textarea
                        value={viewInitialDetailsEditor.form.description}
                        onChange={(e) => setViewInitialDetailsEditor((prev) => ({
                          ...prev,
                          form: { ...prev.form, description: e.target.value },
                        }))}
                        rows={3}
                        data-testid="view-project-description-input"
                      />
                    ) : (
                      <p className="p-3 bg-gray-50 rounded-lg mt-1 text-sm whitespace-pre-wrap" data-testid="view-project-description-value">
                        {viewDialog.project.description || 'Sem descrição inicial registada.'}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Descrição Técnica</Label>
                    {viewInitialDetailsEditor.editing ? (
                      <Textarea
                        value={viewInitialDetailsEditor.form.technical_description}
                        onChange={(e) => setViewInitialDetailsEditor((prev) => ({
                          ...prev,
                          form: { ...prev.form, technical_description: e.target.value },
                        }))}
                        rows={4}
                        data-testid="view-project-technical-description-input"
                      />
                    ) : (
                      <p className="p-3 bg-gray-50 rounded-lg mt-1 text-sm whitespace-pre-wrap" data-testid="view-project-technical-description-value">
                        {viewDialog.project.technical_description || 'Sem descrição técnica registada.'}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Âmbito / Escopo do trabalho</Label>
                    {viewInitialDetailsEditor.editing ? (
                      <Textarea
                        value={viewInitialDetailsEditor.form.scope_of_work}
                        onChange={(e) => setViewInitialDetailsEditor((prev) => ({
                          ...prev,
                          form: { ...prev.form, scope_of_work: e.target.value },
                        }))}
                        rows={4}
                        data-testid="view-project-scope-of-work-input"
                      />
                    ) : (
                      <p className="p-3 bg-gray-50 rounded-lg mt-1 text-sm whitespace-pre-wrap" data-testid="view-project-scope-of-work-value">
                        {viewDialog.project.scope_of_work || 'Sem escopo definido.'}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm font-medium">Notas internas</Label>
                    {viewInitialDetailsEditor.editing ? (
                      <Textarea
                        value={viewInitialDetailsEditor.form.internal_notes}
                        onChange={(e) => setViewInitialDetailsEditor((prev) => ({
                          ...prev,
                          form: { ...prev.form, internal_notes: e.target.value },
                        }))}
                        rows={4}
                        data-testid="view-project-internal-notes-input"
                      />
                    ) : (
                      <p className="p-3 bg-amber-50 border border-amber-200 rounded-lg mt-1 text-sm whitespace-pre-wrap" data-testid="view-project-internal-notes-value">
                        {viewDialog.project.internal_notes || 'Sem notas internas registadas.'}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {viewDialog.project.project_type === 'gps_fleet' && (
                <Card data-testid="project-gps-fleet-details-section">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Truck className="h-4 w-4" />
                      Campos específicos — GPS / Gestão de Frota
                    </CardTitle>
                    <CardDescription>
                      Estrutura operacional específica deste tipo, sem afectar os demais projectos.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {getGpsDetailItems(viewDialog.project.gps_fields || {}).map(([label, value]) => (
                      <div key={label} className="rounded-lg border bg-muted/20 p-3">
                        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
                        <p className="font-medium mt-1">{value}</p>
                      </div>
                    ))}
                    <div className="rounded-lg border bg-muted/20 p-3 md:col-span-2" data-testid="project-gps-questionnaire-preview">
                      <p className="text-xs uppercase tracking-wide text-muted-foreground">
                        Questionário operativo previsto — {getGpsOptionLabel(GPS_OPERATION_OPTIONS, viewDialog.project.gps_fields?.operation_type)}
                      </p>
                      <ul className="mt-2 space-y-1 list-disc pl-4 text-sm text-muted-foreground">
                        {buildGpsQuestionnairePreview(viewDialog.project.gps_fields || {}).slice(0, 8).map((item) => <li key={item}>{item}</li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card data-testid="project-initial-details-history-section">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Histórico visível dos apontamentos iniciais
                  </CardTitle>
                  <CardDescription>
                    Regista alterações às notas internas, descrição, escopo e eventos de faturação da obra.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {viewInitialDetailsHistory.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="project-initial-details-history-empty">Ainda não existem alterações registadas.</p>
                  ) : (
                    <div className="space-y-3">
                      {viewInitialDetailsHistory.slice(0, 8).map((entry) => (
                        <div key={entry.id} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`project-history-entry-${entry.id}`}>
                          <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                            <div>
                              <p className="text-sm font-medium text-slate-900">
                                {getProjectHistoryTitle(entry)}
                              </p>
                              <p className="mt-1 text-xs text-slate-600">Por: {entry.changed_by_name || 'Sistema'}</p>
                              {entry.admin_notes && (
                                <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap" data-testid={`project-history-entry-notes-${entry.id}`}>
                                  {entry.admin_notes}
                                </p>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs text-slate-500">{formatDate(entry.changed_at)}</span>
                              {canManageVisibleHistory && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className={inventoryStyleActionButtonClass}
                                    onClick={() => openEditHistoryEntryDialog(viewDialog.project, entry)}
                                    data-testid={`project-history-entry-edit-${entry.id}`}
                                  >
                                    <Edit2 className="h-4 w-4 mr-1" /> Editar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className={inventoryStyleDangerButtonClass}
                                    onClick={() => setDeleteHistoryEntryDialog({ open: true, project: viewDialog.project, entry, deleting: false })}
                                    data-testid={`project-history-entry-delete-${entry.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" /> Apagar
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {(entry.changed_fields || []).map((field) => (
                              <Badge key={field} variant="outline" className="bg-white text-[11px]">
                                {field}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="project-complementary-items-section">
                <CardHeader className="pb-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Serviços / materiais complementares
                      </CardTitle>
                      <CardDescription>
                        Adicione, edite ou apague linhas complementares da obra. Se associar um artigo, a linha segue também para a pré-faturação.
                      </CardDescription>
                    </div>
                    {canManageComplementaryItems && (
                      <Button
                        size="sm"
                        variant="outline"
                        className={inventoryStyleActionButtonClass}
                        onClick={() => openCreateMaterialDialog(viewDialog.project)}
                        data-testid="view-project-add-complementary-item-button"
                      >
                        <Plus className="h-4 w-4 mr-1" /> Adicionar complemento
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {!(viewDialog.project.materials || []).length ? (
                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-600" data-testid="project-complementary-items-empty">
                      Ainda não existem complementos registados para esta obra.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(viewDialog.project.materials || []).map((material) => (
                        <div key={material.id || `${material.description}-${material.serial_number || ''}`} className="rounded-2xl border border-slate-200 bg-white px-4 py-4" data-testid={`project-complementary-item-${material.id || material.description}`}>
                          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-medium text-slate-900">{material.description || 'Complemento sem descrição'}</p>
                                <Badge variant="outline" data-testid={`project-complementary-item-quantity-${material.id || material.description}`}>
                                  Qtd: {material.quantity || 1} {material.unit || 'un'}
                                </Badge>
                                <Badge className={material.product_id ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}>
                                  {material.product_id ? 'Com artigo associado' : 'Sem artigo associado'}
                                </Badge>
                              </div>
                              {(material.serial_number || material.notes) && (
                                <p className="text-sm text-slate-600" data-testid={`project-complementary-item-meta-${material.id || material.description}`}>
                                  {[material.serial_number ? `S/N: ${material.serial_number}` : null, material.notes].filter(Boolean).join(' • ')}
                                </p>
                              )}
                            </div>
                            {canManageComplementaryItems && (
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={inventoryStyleActionButtonClass}
                                  onClick={() => openEditMaterialDialog(viewDialog.project, material)}
                                  data-testid={`project-complementary-item-edit-${material.id || material.description}`}
                                >
                                  <Edit2 className="h-4 w-4 mr-1" /> Editar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={inventoryStyleDangerButtonClass}
                                  onClick={() => setDeleteMaterialDialog({ open: true, project: viewDialog.project, material, deleting: false })}
                                  data-testid={`project-complementary-item-delete-${material.id || material.description}`}
                                  disabled={!material.id}
                                >
                                  <Trash2 className="h-4 w-4 mr-1" /> Apagar
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="project-billing-link-section">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Faturação / Venda
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={viewDialog.project.billing_status === 'invoiced' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-700'}>
                      {viewDialog.project.billing_status === 'invoiced' ? 'Obra faturada' : 'Ainda não faturada'}
                    </Badge>
                    {viewDialog.project.linked_sale_number && (
                      <Badge variant="outline" data-testid="view-project-linked-sale-number">
                        Venda: {viewDialog.project.linked_sale_number}
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewDialog.project.linked_sale_id ? openLinkedSale(viewDialog.project) : openProjectSaleConversionDialog(viewDialog.project)}
                      disabled={conversionLoadingKey === viewDialog.project.id}
                      data-testid="view-project-convert-to-sale-button"
                    >
                      {conversionLoadingKey === viewDialog.project.id ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
                      {viewDialog.project.linked_sale_id ? 'Abrir venda associada' : 'Converter em venda/fatura'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="project-additional-interventions-section">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Copy className="h-4 w-4" />
                    Intervenções complementares
                  </CardTitle>
                  <CardDescription>
                    Histórico das intervenções adicionais com horário, técnico, notas completas e venda complementar associada.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {!(viewDialog.project.additional_service_interventions || []).length ? (
                    <p className="text-sm text-muted-foreground" data-testid="project-additional-interventions-empty">
                      Ainda não existem intervenções complementares registadas para esta obra.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {(viewDialog.project.additional_service_interventions || []).slice().reverse().map((entry) => (
                        <div key={entry.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid={`project-additional-intervention-${entry.id}`}>
                          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge className="bg-violet-100 text-violet-800">Complementar</Badge>
                                {entry.sale_number && <Badge variant="outline">Venda: {entry.sale_number}</Badge>}
                              </div>
                              <p className="font-medium text-slate-900">{entry.description || 'Serviço complementar'}</p>
                              <div className="grid gap-2 text-sm text-slate-600 md:grid-cols-2 xl:grid-cols-4">
                                <p><strong>Data:</strong> {entry.intervention_date || '—'}</p>
                                <p><strong>Início:</strong> {entry.start_time || '—'}</p>
                                <p><strong>Fim:</strong> {entry.end_time || '—'}</p>
                                <p><strong>Técnico:</strong> {entry.technician_name || '—'}</p>
                              </div>
                              {entry.reason && <p className="text-sm text-slate-700"><strong>Motivo:</strong> {entry.reason}</p>}
                              {entry.notes && <p className="text-sm text-slate-700 whitespace-pre-wrap"><strong>Notas:</strong> {entry.notes}</p>}
                              <p className="text-xs text-slate-500">Registado em {formatDate(entry.created_at)} por {entry.created_by_name || 'Sistema'}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {entry.sale_id && (
                                <Button size="sm" variant="outline" className={inventoryStyleActionButtonClass} onClick={() => openSpecificSale(entry.sale_id, viewDialog.project.id)} data-testid={`project-additional-intervention-open-sale-${entry.id}`}>
                                  <FileText className="mr-2 h-4 w-4" /> Abrir venda
                                </Button>
                              )}
                              {canManageInterventions && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className={inventoryStyleActionButtonClass}
                                    onClick={() => openEditInterventionDialog(viewDialog.project, entry)}
                                    data-testid={`project-additional-intervention-edit-${entry.id}`}
                                  >
                                    <Edit2 className="mr-2 h-4 w-4" /> Editar
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className={inventoryStyleDangerButtonClass}
                                    onClick={() => setDeleteInterventionDialog({ open: true, project: viewDialog.project, entry, deleting: false })}
                                    data-testid={`project-additional-intervention-delete-${entry.id}`}
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" /> Apagar
                                  </Button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Signatures Status */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Pen className="h-4 w-4" />
                    Estado das Assinaturas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-3 border rounded-lg bg-transparent ${viewDialog.project.is_signed_by_client ? 'border-green-200' : 'border-orange-200'}`} data-testid="view-project-client-signature-card">
                      <p className="text-sm font-medium">Cliente</p>
                      {viewDialog.project.is_signed_by_client ? (
                        <div>
                          <Badge className="bg-green-100 text-green-800 mt-1">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Assinado
                          </Badge>
                          {viewDialog.project.client_signed_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDate(viewDialog.project.client_signed_at)}
                            </p>
                          )}
                          {getLatestSignatureByRole(viewDialog.project, 'client')?.signer_name && (
                            <p className="text-xs text-slate-600 mt-1" data-testid="view-project-client-signer-name">
                              {getLatestSignatureByRole(viewDialog.project, 'client')?.signer_name}
                            </p>
                          )}
                          {canClearProjectSignatures && (
                            <Button
                              size="sm"
                              variant="outline"
                              className={`mt-2 ${inventoryStyleDangerButtonClass}`}
                              onClick={() => handleClearSignatureSlot(viewDialog.project, 'client')}
                              data-testid="view-project-clear-client-signature-button"
                            >
                              <Trash2 className="h-4 w-4 mr-1" /> Limpar assinatura do cliente
                            </Button>
                          )}
                        </div>
                      ) : (
                        <Badge className="bg-orange-100 text-orange-800 mt-1">
                          <Clock className="h-3 w-3 mr-1" />
                          Pendente
                        </Badge>
                      )}
                    </div>
                    <div className={`p-3 border rounded-lg bg-transparent ${viewDialog.project.is_signed_by_technician ? 'border-green-200' : 'border-slate-200'}`} data-testid="view-project-technician-signature-card">
                      <p className="text-sm font-medium">Técnico</p>
                      {viewDialog.project.is_signed_by_technician ? (
                        <div>
                          <Badge className="bg-green-100 text-green-800 mt-1">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Assinado
                          </Badge>
                          {viewDialog.project.technician_signed_at && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDate(viewDialog.project.technician_signed_at)}
                            </p>
                          )}
                          {getLatestSignatureByRoles(viewDialog.project, ['technician', 'lead', 'supervisor'])?.signer_name && (
                            <p className="text-xs text-slate-600 mt-1" data-testid="view-project-technician-signer-name">
                              {getLatestSignatureByRoles(viewDialog.project, ['technician', 'lead', 'supervisor'])?.signer_name}
                            </p>
                          )}
                          {canClearProjectSignatures && (
                            <Button
                              size="sm"
                              variant="outline"
                              className={`mt-2 ${inventoryStyleDangerButtonClass}`}
                              onClick={() => handleClearSignatureSlot(viewDialog.project, 'technician')}
                              data-testid="view-project-clear-technician-signature-button"
                            >
                              <Trash2 className="h-4 w-4 mr-1" /> Limpar assinatura técnica
                            </Button>
                          )}
                        </div>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-800 mt-1">
                          <Clock className="h-3 w-3 mr-1" />
                          Pendente
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-2">
                {viewDialog.project.status === 'draft' && (
                  <Button size="sm" className={inventoryStyleActionButtonClass} onClick={() => handleStatusChange(viewDialog.project.id, 'open')}>
                    <Flag className="h-4 w-4 mr-1" /> Abrir Obra
                  </Button>
                )}
                {viewDialog.project.status === 'open' && (
                  <Button size="sm" className={inventoryStyleActionButtonClass} onClick={() => handleStatusChange(viewDialog.project.id, 'in_progress')}>
                    <Play className="h-4 w-4 mr-1" /> Iniciar Execução
                  </Button>
                )}
                {viewDialog.project.status === 'in_progress' && (
                  <Button size="sm" variant="outline" className={inventoryStyleActionButtonClass} onClick={() => handleStatusChange(viewDialog.project.id, 'pending_signature')}>
                    <Pen className="h-4 w-4 mr-1" /> Aguardar Assinatura
                  </Button>
                )}
                {viewDialog.project.status === 'completed' && (
                  <Button size="sm" className={inventoryStyleActionButtonClass} onClick={() => handleStatusChange(viewDialog.project.id, 'delivered')}>
                    <CheckCircle className="h-4 w-4 mr-1" /> Marcar Entregue
                  </Button>
                )}
                <Button 
                  size="sm" 
                  variant="outline"
                  className={`${inventoryStyleActionButtonClass} ${viewDialog.project.is_signed_by_client ? 'border-green-300 text-green-700 hover:bg-green-50' : 'border-orange-300 text-orange-700 hover:bg-orange-50'}`}
                  onClick={() => {
                    closeViewProjectDialog();
                    openSignatureDialog(viewDialog.project, 'client');
                  }}
                  data-testid="view-project-sign-client-button"
                >
                  <Pen className="h-4 w-4 mr-1" /> {viewDialog.project.is_signed_by_client ? 'Editar Assinatura Cliente' : 'Obter Assinatura Cliente'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className={inventoryStyleActionButtonClass}
                  onClick={() => {
                    closeViewProjectDialog();
                    openSignatureDialog(viewDialog.project, 'technician');
                  }}
                  data-testid="view-project-sign-technician-button"
                >
                  <Pen className="h-4 w-4 mr-1" /> {viewDialog.project.is_signed_by_technician ? 'Editar Assinatura Técnica' : 'Assinar (Técnico)'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className={inventoryStyleActionButtonClass}
                  onClick={() => {
                    setStoreDialog({
                      open: true,
                      project: viewDialog.project,
                      storeId: viewDialog.project.store_id || '',
                    });
                  }}
                  data-testid="view-project-update-store-button"
                >
                  <Store className="h-4 w-4 mr-1" /> Alterar Loja
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className={inventoryStyleActionButtonClass}
                  onClick={() => handleRunIntegrityCheck(viewDialog.project)}
                  data-testid="view-project-integrity-check-button"
                >
                  <ShieldCheck className="h-4 w-4 mr-1" /> Check Completo
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className={inventoryStyleActionButtonClass}
                  onClick={() => openDuplicateServiceDialog(viewDialog.project)}
                  data-testid="view-project-duplicate-service-button"
                >
                  <Copy className="h-4 w-4 mr-1" /> Duplicar Serviço
                </Button>
                {canClearProjectSignatures && Boolean((viewDialog.project.signatures || []).length) && (
                  <Button
                    size="sm"
                    variant="outline"
                    className={inventoryStyleDangerButtonClass}
                    onClick={() => handleClearAllProjectSignatures(viewDialog.project)}
                    data-testid="view-project-clear-all-signatures-button"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Limpar todas as assinaturas
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className={inventoryStyleActionButtonClass}
                  onClick={() => setPortalDialog({
                    open: true,
                    project: viewDialog.project,
                    allowedEmailsRaw: viewDialog.project.customer_email || '',
                    expiresInDays: 7,
                    generatedLink: '',
                    generatedPath: '',
                    expiresAt: '',
                    creating: false,
                  })}
                  data-testid="view-project-customer-portal-button"
                >
                  <Link2 className="h-4 w-4 mr-1" /> Acesso Cliente
                </Button>
                <Button size="sm" variant="outline" className={inventoryStyleActionButtonClass} onClick={() => handlePrintCheck(viewDialog.project)}>
                  <Printer className="h-4 w-4 mr-1" /> Imprimir
                </Button>
                {canDeleteProjects && (
                  <Button
                    size="sm"
                    variant="outline"
                    className={inventoryStyleDangerButtonClass}
                    onClick={() => openDeleteProjectDialog(viewDialog.project)}
                    data-testid="view-project-delete-button"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Apagar Obra
                  </Button>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-xl border-slate-300 bg-white px-4 text-slate-700 shadow-sm transition-transform hover:-translate-y-0.5 hover:border-slate-400 hover:bg-slate-50"
              onClick={closeViewProjectDialog}
            >
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={materialDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setMaterialDialog({ open: false, mode: 'create', project: null, saving: false, form: EMPTY_MATERIAL_FORM });
          }
        }}
      >
        <DialogContent className="max-w-2xl" aria-describedby="project-complementary-item-dialog-description" data-testid="project-complementary-item-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-500" />
              {materialDialog.mode === 'edit' ? 'Editar complemento' : 'Adicionar complemento'}
            </DialogTitle>
            <DialogDescription id="project-complementary-item-dialog-description">
              {materialDialog.project?.project_number} — registe material ou serviço complementar desta obra.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="project-complementary-item-product">Artigo associado (opcional)</Label>
              <div data-testid="project-complementary-item-product-field">
                <ProductCombobox
                  products={productCatalog}
                  value={materialDialog.form.product_id || ''}
                  onValueChange={(value) => {
                    const product = productCatalog.find((item) => item.id === value);
                    setMaterialDialog((prev) => ({
                      ...prev,
                      form: {
                        ...prev.form,
                        product_id: value,
                        item_id: '',
                        description: product?.name || prev.form.description,
                      },
                    }));
                  }}
                  placeholder="Seleccionar artigo do inventário"
                />
              </div>
              <p className="mt-1 text-xs text-slate-500" data-testid="project-complementary-item-product-help">
                {selectedMaterialProduct
                  ? `Artigo seleccionado: ${selectedMaterialProduct.name} (${selectedMaterialProduct.sku || 'sem SKU'})`
                  : 'Se não associar um artigo, a linha ficará apenas para registo/documentação.'}
              </p>
              {materialDialog.form.product_id && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-2 px-0 text-red-600 hover:bg-transparent hover:text-red-700"
                  onClick={() => setMaterialDialog((prev) => ({
                    ...prev,
                    form: { ...prev.form, product_id: '', item_id: '' },
                  }))}
                  data-testid="project-complementary-item-clear-product-button"
                >
                  Remover artigo associado
                </Button>
              )}
            </div>

            <div>
              <Label htmlFor="project-complementary-item-description-input">Descrição *</Label>
              <Input
                id="project-complementary-item-description-input"
                value={materialDialog.form.description || ''}
                onChange={(e) => setMaterialDialog((prev) => ({
                  ...prev,
                  form: { ...prev.form, description: e.target.value },
                }))}
                placeholder="Ex: Sensor adicional, antena, visita extra"
                data-testid="project-complementary-item-description-input"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="project-complementary-item-quantity-input">Quantidade *</Label>
                <Input
                  id="project-complementary-item-quantity-input"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={materialDialog.form.quantity ?? 1}
                  onChange={(e) => setMaterialDialog((prev) => ({
                    ...prev,
                    form: { ...prev.form, quantity: e.target.value },
                  }))}
                  data-testid="project-complementary-item-quantity-input"
                />
              </div>
              <div>
                <Label htmlFor="project-complementary-item-unit-input">Unidade</Label>
                <Input
                  id="project-complementary-item-unit-input"
                  value={materialDialog.form.unit || 'un'}
                  onChange={(e) => setMaterialDialog((prev) => ({
                    ...prev,
                    form: { ...prev.form, unit: e.target.value },
                  }))}
                  placeholder="un, m, kit, serviço"
                  data-testid="project-complementary-item-unit-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="project-complementary-item-serial-input">Número de série / referência</Label>
              <Input
                id="project-complementary-item-serial-input"
                value={materialDialog.form.serial_number || ''}
                onChange={(e) => setMaterialDialog((prev) => ({
                  ...prev,
                  form: { ...prev.form, serial_number: e.target.value },
                }))}
                placeholder="Opcional"
                data-testid="project-complementary-item-serial-input"
              />
            </div>

            <div>
              <Label htmlFor="project-complementary-item-notes-input">Notas</Label>
              <Textarea
                id="project-complementary-item-notes-input"
                rows={4}
                value={materialDialog.form.notes || ''}
                onChange={(e) => setMaterialDialog((prev) => ({
                  ...prev,
                  form: { ...prev.form, notes: e.target.value },
                }))}
                placeholder="Observações técnicas, lote, motivo do complemento..."
                data-testid="project-complementary-item-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMaterialDialog({ open: false, mode: 'create', project: null, saving: false, form: EMPTY_MATERIAL_FORM })}
              data-testid="project-complementary-item-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveMaterial}
              disabled={materialDialog.saving}
              data-testid="project-complementary-item-save-button"
            >
              {materialDialog.saving ? 'A guardar...' : materialDialog.mode === 'edit' ? 'Guardar alterações' : 'Adicionar complemento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteMaterialDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteMaterialDialog({ open: false, project: null, material: null, deleting: false });
          }
        }}
      >
        <DialogContent className="max-w-md" aria-describedby="project-complementary-item-delete-dialog-description" data-testid="project-complementary-item-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" /> Apagar complemento
            </DialogTitle>
            <DialogDescription id="project-complementary-item-delete-dialog-description">
              Esta acção remove a linha complementar da obra e da futura pré-faturação.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="project-complementary-item-delete-warning">
            Complemento: <strong>{deleteMaterialDialog.material?.description || '—'}</strong>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteMaterialDialog({ open: false, project: null, material: null, deleting: false })}
              data-testid="project-complementary-item-delete-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteMaterial}
              disabled={deleteMaterialDialog.deleting}
              data-testid="project-complementary-item-delete-confirm-button"
            >
              {deleteMaterialDialog.deleting ? 'A apagar...' : 'Confirmar apagar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteProjectDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteProjectDialog({ open: false, project: null, deleting: false });
          }
        }}
      >
        <DialogContent className="max-w-md" aria-describedby="project-delete-dialog-description" data-testid="project-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" /> Apagar obra
            </DialogTitle>
            <DialogDescription id="project-delete-dialog-description">
              A obra será removida da listagem activa e os links públicos activos serão revogados.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <p data-testid="project-delete-dialog-project-number">{deleteProjectDialog.project?.project_number || '—'}</p>
            <p data-testid="project-delete-dialog-project-title">{deleteProjectDialog.project?.title || '—'}</p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteProjectDialog({ open: false, project: null, deleting: false })}
              data-testid="project-delete-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteProject}
              disabled={deleteProjectDialog.deleting}
              data-testid="project-delete-confirm-button"
            >
              {deleteProjectDialog.deleting ? 'A apagar...' : 'Confirmar apagar obra'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={historyEntryDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setHistoryEntryDialog({
              open: false,
              project: null,
              entry: null,
              saving: false,
              form: { display_title: '', admin_notes: '', changed_by_name: '', changed_at: '', changed_fields_text: '' },
            });
          }
        }}
      >
        <DialogContent className="max-w-xl" aria-describedby="project-history-entry-dialog-description" data-testid="project-history-entry-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-amber-500" /> Editar histórico visível
            </DialogTitle>
            <DialogDescription id="project-history-entry-dialog-description">
              Ajuste o título visível, os campos resumidos e as notas administrativas desta entrada.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="project-history-entry-title-input">Título visível</Label>
              <Input
                id="project-history-entry-title-input"
                value={historyEntryDialog.form.display_title}
                onChange={(e) => setHistoryEntryDialog((prev) => ({
                  ...prev,
                  form: { ...prev.form, display_title: e.target.value },
                }))}
                data-testid="project-history-entry-title-input"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="project-history-entry-author-input">Alterado por</Label>
                <Input
                  id="project-history-entry-author-input"
                  value={historyEntryDialog.form.changed_by_name}
                  onChange={(e) => setHistoryEntryDialog((prev) => ({
                    ...prev,
                    form: { ...prev.form, changed_by_name: e.target.value },
                  }))}
                  data-testid="project-history-entry-author-input"
                />
              </div>
              <div>
                <Label htmlFor="project-history-entry-date-input">Data/hora</Label>
                <Input
                  id="project-history-entry-date-input"
                  value={historyEntryDialog.form.changed_at}
                  onChange={(e) => setHistoryEntryDialog((prev) => ({
                    ...prev,
                    form: { ...prev.form, changed_at: e.target.value },
                  }))}
                  placeholder="2026-03-13T21:00:00+02:00"
                  data-testid="project-history-entry-date-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="project-history-entry-fields-input">Campos (separados por vírgula)</Label>
              <Input
                id="project-history-entry-fields-input"
                value={historyEntryDialog.form.changed_fields_text}
                onChange={(e) => setHistoryEntryDialog((prev) => ({
                  ...prev,
                  form: { ...prev.form, changed_fields_text: e.target.value },
                }))}
                data-testid="project-history-entry-fields-input"
              />
            </div>
            <div>
              <Label htmlFor="project-history-entry-notes-input">Notas administrativas</Label>
              <Textarea
                id="project-history-entry-notes-input"
                rows={4}
                value={historyEntryDialog.form.admin_notes}
                onChange={(e) => setHistoryEntryDialog((prev) => ({
                  ...prev,
                  form: { ...prev.form, admin_notes: e.target.value },
                }))}
                data-testid="project-history-entry-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setHistoryEntryDialog({
                open: false,
                project: null,
                entry: null,
                saving: false,
                form: { display_title: '', admin_notes: '', changed_by_name: '', changed_at: '', changed_fields_text: '' },
              })}
              data-testid="project-history-entry-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveHistoryEntry}
              disabled={historyEntryDialog.saving}
              data-testid="project-history-entry-save-button"
            >
              {historyEntryDialog.saving ? 'A guardar...' : 'Guardar histórico'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteHistoryEntryDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteHistoryEntryDialog({ open: false, project: null, entry: null, deleting: false });
          }
        }}
      >
        <DialogContent className="max-w-md" aria-describedby="project-history-entry-delete-dialog-description" data-testid="project-history-entry-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" /> Apagar histórico visível
            </DialogTitle>
            <DialogDescription id="project-history-entry-delete-dialog-description">
              Esta acção remove permanentemente a entrada seleccionada do histórico visível da obra.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="project-history-entry-delete-warning">
            {getProjectHistoryTitle(deleteHistoryEntryDialog.entry)}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteHistoryEntryDialog({ open: false, project: null, entry: null, deleting: false })}
              data-testid="project-history-entry-delete-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteHistoryEntry}
              disabled={deleteHistoryEntryDialog.deleting}
              data-testid="project-history-entry-delete-confirm-button"
            >
              {deleteHistoryEntryDialog.deleting ? 'A apagar...' : 'Confirmar apagar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={interventionDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setInterventionDialog({
              open: false,
              project: null,
              entry: null,
              saving: false,
              form: { description: '', reason: '', notes: '', intervention_date: '', start_time: '', end_time: '', technician_name: '' },
            });
          }
        }}
      >
        <DialogContent className="max-w-xl" aria-describedby="project-intervention-dialog-description" data-testid="project-intervention-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-violet-600" /> Editar intervenção complementar
            </DialogTitle>
            <DialogDescription id="project-intervention-dialog-description">
              Ajuste os dados da intervenção complementar registada nesta obra.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="project-intervention-description-input">Descrição</Label>
              <Input
                id="project-intervention-description-input"
                value={interventionDialog.form.description}
                onChange={(e) => setInterventionDialog((prev) => ({
                  ...prev,
                  form: { ...prev.form, description: e.target.value },
                }))}
                data-testid="project-intervention-description-input"
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="project-intervention-date-input">Data</Label>
                <Input
                  id="project-intervention-date-input"
                  type="date"
                  value={interventionDialog.form.intervention_date}
                  onChange={(e) => setInterventionDialog((prev) => ({
                    ...prev,
                    form: { ...prev.form, intervention_date: e.target.value },
                  }))}
                  data-testid="project-intervention-date-input"
                />
              </div>
              <div>
                <Label htmlFor="project-intervention-technician-input">Técnico</Label>
                <Input
                  id="project-intervention-technician-input"
                  value={interventionDialog.form.technician_name}
                  onChange={(e) => setInterventionDialog((prev) => ({
                    ...prev,
                    form: { ...prev.form, technician_name: e.target.value },
                  }))}
                  data-testid="project-intervention-technician-input"
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="project-intervention-start-time-input">Hora inicial</Label>
                <Input
                  id="project-intervention-start-time-input"
                  type="time"
                  value={interventionDialog.form.start_time}
                  onChange={(e) => setInterventionDialog((prev) => ({
                    ...prev,
                    form: { ...prev.form, start_time: e.target.value },
                  }))}
                  data-testid="project-intervention-start-time-input"
                />
              </div>
              <div>
                <Label htmlFor="project-intervention-end-time-input">Hora final</Label>
                <Input
                  id="project-intervention-end-time-input"
                  type="time"
                  value={interventionDialog.form.end_time}
                  onChange={(e) => setInterventionDialog((prev) => ({
                    ...prev,
                    form: { ...prev.form, end_time: e.target.value },
                  }))}
                  data-testid="project-intervention-end-time-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="project-intervention-reason-input">Motivo</Label>
              <Input
                id="project-intervention-reason-input"
                value={interventionDialog.form.reason}
                onChange={(e) => setInterventionDialog((prev) => ({
                  ...prev,
                  form: { ...prev.form, reason: e.target.value },
                }))}
                data-testid="project-intervention-reason-input"
              />
            </div>
            <div>
              <Label htmlFor="project-intervention-notes-input">Notas</Label>
              <Textarea
                id="project-intervention-notes-input"
                rows={4}
                value={interventionDialog.form.notes}
                onChange={(e) => setInterventionDialog((prev) => ({
                  ...prev,
                  form: { ...prev.form, notes: e.target.value },
                }))}
                data-testid="project-intervention-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setInterventionDialog({
                open: false,
                project: null,
                entry: null,
                saving: false,
                form: { description: '', reason: '', notes: '', intervention_date: '', start_time: '', end_time: '', technician_name: '' },
              })}
              data-testid="project-intervention-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveIntervention}
              disabled={interventionDialog.saving}
              data-testid="project-intervention-save-button"
            >
              {interventionDialog.saving ? 'A guardar...' : 'Guardar intervenção'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteInterventionDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteInterventionDialog({ open: false, project: null, entry: null, deleting: false });
          }
        }}
      >
        <DialogContent className="max-w-md" aria-describedby="project-intervention-delete-dialog-description" data-testid="project-intervention-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" /> Apagar intervenção complementar
            </DialogTitle>
            <DialogDescription id="project-intervention-delete-dialog-description">
              Esta acção remove a intervenção complementar do histórico da obra.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="project-intervention-delete-warning">
            {deleteInterventionDialog.entry?.description || 'Intervenção complementar'}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteInterventionDialog({ open: false, project: null, entry: null, deleting: false })}
              data-testid="project-intervention-delete-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteIntervention}
              disabled={deleteInterventionDialog.deleting}
              data-testid="project-intervention-delete-confirm-button"
            >
              {deleteInterventionDialog.deleting ? 'A apagar...' : 'Confirmar apagar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={conversionDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setConversionDialog({
              open: false,
              loading: false,
              submitting: false,
              project: null,
              location: null,
              items: [],
              warnings: [],
              suggestedNotes: '',
              additionalNotes: '',
              vatOptions: [],
            });
          }
        }}
      >
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="project-sale-conversion-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              Preparar conversão Obra → Venda Draft
            </DialogTitle>
            <DialogDescription>
              Ajuste preços finais, quantidades, descontos e IVA antes de abrir a venda em rascunho para revisão.
            </DialogDescription>
          </DialogHeader>

          {conversionDialog.loading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">A preparar linhas da conversão...</div>
          ) : (
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Obra</p>
                  <p className="mt-1 font-medium text-slate-900">{conversionDialog.project?.project_number}</p>
                  <p className="text-sm text-slate-600">{conversionDialog.project?.title}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Loja / Venda</p>
                  <p className="mt-1 font-medium text-slate-900">{conversionDialog.location?.name || conversionDialog.project?.store_name || 'Sem localização'}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Resumo</p>
                  <p className="mt-1 text-sm text-slate-700">Subtotal líquido: <strong>{formatMoney(conversionSummary.net)}</strong></p>
                  <p className="text-sm text-slate-700">Total estimado: <strong>{formatMoney(conversionSummary.gross)}</strong></p>
                </div>
              </div>

              {conversionDialog.warnings.length > 0 && (
                <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="project-sale-conversion-warning-box">
                  <p className="font-medium text-amber-900">Avisos da conversão</p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-amber-800">
                    {conversionDialog.warnings.map((warning, index) => (
                      <li key={`${warning}-${index}`}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-4">
                {conversionDialog.items.map((item, index) => (
                  <Card key={`${item.product_id}-${index}`} data-testid={`project-sale-conversion-line-${index}`}>
                    <CardHeader className="pb-3">
                      <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                          <CardTitle className="text-base">{item.product_name}</CardTitle>
                          <CardDescription>{item.description || 'Sem descrição adicional'}</CardDescription>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline">{item.item_origin === 'service' ? 'Serviço' : 'Material'}</Badge>
                          <Badge className="bg-slate-100 text-slate-700">{item.price_strategy === 'manual_service' ? 'Preço manual do serviço' : 'Preço manual do material'}</Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="grid gap-4 lg:grid-cols-6">
                      <div>
                        <Label htmlFor={`conversion-qty-${index}`}>Qtd.</Label>
                        <Input id={`conversion-qty-${index}`} type="number" min="1" value={item.quantity}
                          onChange={(e) => updateConversionItemField(index, 'quantity', e.target.value)}
                          data-testid={`project-sale-conversion-quantity-input-${index}`}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`conversion-price-${index}`}>Preço Unitário</Label>
                        <Input id={`conversion-price-${index}`} type="number" min="0" step="0.01" value={item.unit_price}
                          onChange={(e) => updateConversionItemField(index, 'unit_price', e.target.value)}
                          data-testid={`project-sale-conversion-unit-price-input-${index}`}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`conversion-discount-${index}`}>Desconto (valor)</Label>
                        <Input id={`conversion-discount-${index}`} type="number" min="0" step="0.01" value={item.discount}
                          onChange={(e) => updateConversionItemField(index, 'discount', e.target.value)}
                          data-testid={`project-sale-conversion-discount-input-${index}`}
                        />
                      </div>
                      <div>
                        <Label htmlFor={`conversion-discount-percent-${index}`}>Desconto %</Label>
                        <Input id={`conversion-discount-percent-${index}`} type="number" min="0" max="100" step="0.01" value={item.discount_percent}
                          onChange={(e) => updateConversionItemField(index, 'discount_percent', e.target.value)}
                          data-testid={`project-sale-conversion-discount-percent-input-${index}`}
                        />
                      </div>
                      <div>
                        <Label>Regime IVA</Label>
                        <Select value={item.vat_regime} onValueChange={(value) => updateConversionItemField(index, 'vat_regime', value)}>
                          <SelectTrigger data-testid={`project-sale-conversion-vat-regime-select-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(conversionDialog.vatOptions || []).map((option) => (
                              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor={`conversion-vat-rate-${index}`}>Taxa IVA %</Label>
                        <Input id={`conversion-vat-rate-${index}`} type="number" min="0" step="0.01" value={item.vat_rate}
                          onChange={(e) => updateConversionItemField(index, 'vat_rate', e.target.value)}
                          data-testid={`project-sale-conversion-vat-rate-input-${index}`}
                        />
                      </div>
                      <div className="lg:col-span-6 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span>Total líquido estimado: <strong>{formatMoney(getConversionLineNetTotal(item))}</strong></span>
                          <span>Total final estimado: <strong>{formatMoney(getConversionLineGrossTotal(item))}</strong></span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="project-sale-conversion-additional-notes">Observações adicionais para a venda</Label>
                <Textarea
                  id="project-sale-conversion-additional-notes"
                  value={conversionDialog.additionalNotes}
                  onChange={(e) => setConversionDialog((prev) => ({ ...prev, additionalNotes: e.target.value }))}
                  rows={4}
                  data-testid="project-sale-conversion-additional-notes-input"
                />
                {conversionDialog.suggestedNotes && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    <p className="font-medium text-slate-900">Notas base sugeridas</p>
                    <p className="mt-2 whitespace-pre-wrap">{conversionDialog.suggestedNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConversionDialog({
                open: false,
                loading: false,
                submitting: false,
                project: null,
                location: null,
                items: [],
                warnings: [],
                suggestedNotes: '',
                additionalNotes: '',
                vatOptions: [],
              })}
              data-testid="project-sale-conversion-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirmProjectConversion}
              disabled={conversionDialog.loading || conversionDialog.submitting || conversionDialog.items.length === 0}
              data-testid="project-sale-conversion-confirm-button"
            >
              {conversionDialog.submitting ? 'A criar draft...' : 'Criar venda draft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={duplicateServiceDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setDuplicateServiceDialog({
              open: false,
              loading: false,
              submitting: false,
              project: null,
              location: null,
              item: null,
              suggestedReason: '',
              revisitReason: '',
              additionalNotes: '',
              interventionDate: '',
              startTime: '',
              endTime: '',
              technicianName: '',
              vatOptions: [],
            });
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="project-duplicate-service-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-emerald-600" />
              Duplicar serviço em nova venda complementar
            </DialogTitle>
            <DialogDescription>
              Crie uma nova intervenção cobrável ligada à mesma obra e gere uma venda draft complementar.
            </DialogDescription>
          </DialogHeader>

          {duplicateServiceDialog.loading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">A preparar serviço adicional...</div>
          ) : duplicateServiceDialog.item ? (
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Obra</p>
                  <p className="mt-1 font-medium text-slate-900">{duplicateServiceDialog.project?.project_number}</p>
                  <p className="text-sm text-slate-600">{duplicateServiceDialog.project?.title}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Venda complementar</p>
                  <p className="mt-1 font-medium text-slate-900">Novo draft</p>
                  <p className="text-sm text-slate-600">Ligado à mesma obra/projecto</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Total estimado</p>
                  <p className="mt-1 font-medium text-slate-900">{formatMoney(getConversionLineGrossTotal(duplicateServiceDialog.item))}</p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="space-y-2">
                  <Label>Data da nova intervenção</Label>
                  <Input
                    type="date"
                    value={duplicateServiceDialog.interventionDate}
                    onChange={(e) => setDuplicateServiceDialog((prev) => ({ ...prev, interventionDate: e.target.value }))}
                    data-testid="duplicate-service-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hora inicial</Label>
                  <Input
                    type="time"
                    value={duplicateServiceDialog.startTime}
                    onChange={(e) => setDuplicateServiceDialog((prev) => ({ ...prev, startTime: e.target.value }))}
                    data-testid="duplicate-service-start-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hora final</Label>
                  <Input
                    type="time"
                    value={duplicateServiceDialog.endTime}
                    onChange={(e) => setDuplicateServiceDialog((prev) => ({ ...prev, endTime: e.target.value }))}
                    data-testid="duplicate-service-end-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Técnico responsável</Label>
                  <Input
                    value={duplicateServiceDialog.technicianName}
                    onChange={(e) => setDuplicateServiceDialog((prev) => ({ ...prev, technicianName: e.target.value }))}
                    placeholder="Nome do técnico"
                    data-testid="duplicate-service-technician-input"
                  />
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <div className="space-y-2 lg:col-span-2">
                  <Label>Descrição do serviço adicional</Label>
                  <Textarea
                    value={duplicateServiceDialog.item.description || ''}
                    onChange={(e) => updateDuplicateServiceField('description', e.target.value)}
                    rows={3}
                    data-testid="duplicate-service-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Motivo / nova intervenção</Label>
                  <Textarea
                    value={duplicateServiceDialog.revisitReason}
                    onChange={(e) => setDuplicateServiceDialog((prev) => ({ ...prev, revisitReason: e.target.value }))}
                    rows={3}
                    data-testid="duplicate-service-reason-input"
                  />
                  {duplicateServiceDialog.suggestedReason && (
                    <p className="text-xs text-slate-500">Sugestão: {duplicateServiceDialog.suggestedReason}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Observações adicionais</Label>
                  <Textarea
                    value={duplicateServiceDialog.additionalNotes}
                    onChange={(e) => setDuplicateServiceDialog((prev) => ({ ...prev, additionalNotes: e.target.value }))}
                    rows={3}
                    data-testid="duplicate-service-additional-notes-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quantidade</Label>
                  <Input type="number" min="1" value={duplicateServiceDialog.item.quantity}
                    onChange={(e) => updateDuplicateServiceField('quantity', e.target.value)}
                    data-testid="duplicate-service-quantity-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Preço Unitário</Label>
                  <Input type="number" min="0" step="0.01" value={duplicateServiceDialog.item.unit_price}
                    onChange={(e) => updateDuplicateServiceField('unit_price', e.target.value)}
                    data-testid="duplicate-service-unit-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Desconto valor</Label>
                  <Input type="number" min="0" step="0.01" value={duplicateServiceDialog.item.discount}
                    onChange={(e) => updateDuplicateServiceField('discount', e.target.value)}
                    data-testid="duplicate-service-discount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Desconto %</Label>
                  <Input type="number" min="0" max="100" step="0.01" value={duplicateServiceDialog.item.discount_percent}
                    onChange={(e) => updateDuplicateServiceField('discount_percent', e.target.value)}
                    data-testid="duplicate-service-discount-percent-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Regime IVA</Label>
                  <Select value={duplicateServiceDialog.item.vat_regime} onValueChange={(value) => updateDuplicateServiceField('vat_regime', value)}>
                    <SelectTrigger data-testid="duplicate-service-vat-regime-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(duplicateServiceDialog.vatOptions || []).map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Taxa IVA %</Label>
                  <Input type="number" min="0" step="0.01" value={duplicateServiceDialog.item.vat_rate}
                    onChange={(e) => updateDuplicateServiceField('vat_rate', e.target.value)}
                    data-testid="duplicate-service-vat-rate-input"
                  />
                </div>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDuplicateServiceDialog({
                open: false,
                loading: false,
                submitting: false,
                project: null,
                location: null,
                item: null,
                suggestedReason: '',
                revisitReason: '',
                additionalNotes: '',
              interventionDate: '',
              startTime: '',
              endTime: '',
              technicianName: '',
                vatOptions: [],
              })}
              data-testid="duplicate-service-cancel-button"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateDuplicateServiceSale}
              disabled={
                duplicateServiceDialog.loading ||
                duplicateServiceDialog.submitting ||
                !duplicateServiceDialog.item ||
                !duplicateServiceDialog.interventionDate ||
                !duplicateServiceDialog.startTime ||
                !duplicateServiceDialog.endTime ||
                !duplicateServiceDialog.technicianName
              }
              data-testid="duplicate-service-confirm-button"
            >
              {duplicateServiceDialog.submitting ? 'A criar venda...' : 'Criar venda complementar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SIGNATURE DIALOG - FUNCIONAL COM PERSISTÊNCIA */}
      <Dialog
        open={signatureDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setSignatureDialog({ open: false, project: null, role: 'client' });
            setSignatureSignerName('');
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pen className="h-5 w-5 text-orange-500" />
              Assinatura Digital
            </DialogTitle>
            <DialogDescription>
              Obra: {signatureDialog.project?.project_number} - {signatureDialog.project?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Signatário *</Label>
              <Input
                id="signer-name"
                placeholder="Nome completo de quem está a assinar"
                value={signatureSignerName}
                onChange={(e) => setSignatureSignerName(e.target.value)}
                data-testid="signature-signer-name-input"
              />
            </div>
            <div>
              <Label>Tipo de Assinatura</Label>
              <Select 
                value={signatureDialog.role} 
                onValueChange={(v) => {
                  const existingSignature = getLatestSignatureByRole(signatureDialog.project, v);
                  const fallbackName = v === 'client' ? (signatureDialog.project?.customer_name || '') : '';
                  setSignatureSignerName(existingSignature?.signer_name || fallbackName);
                  setSignatureDialog({ ...signatureDialog, role: v });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Cliente</SelectItem>
                  <SelectItem value="technician">Técnico</SelectItem>
                  <SelectItem value="lead">Responsável / Supervisor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {getLatestSignatureByRole(signatureDialog.project, signatureDialog.role) && (
              <div className="p-2 text-xs rounded-md border border-slate-200 bg-transparent text-slate-700" data-testid="signature-replace-warning">
                Já existe assinatura para este perfil. Ao guardar, a assinatura anterior será substituída.
              </div>
            )}
            <div>
              <Label>Assinatura *</Label>
              <p className="text-xs text-muted-foreground mb-2">Desenhe a sua assinatura na área abaixo (use o mouse ou toque)</p>
              <div 
                className="border-2 border-dashed rounded-lg bg-transparent overflow-hidden" 
                style={{ width: '100%', height: '170px' }}
                data-testid="signature-canvas-container"
              >
                <canvas
                  ref={signatureCanvasRef}
                  className="cursor-crosshair"
                  style={{ 
                    backgroundColor: 'transparent',
                    touchAction: 'none',
                    width: '100%',
                    height: '160px',
                    display: 'block',
                    margin: '4px auto'
                  }}
                />
              </div>
              <Button variant="ghost" size="sm" className="mt-1" onClick={clearSignature}>
                <Trash2 className="h-4 w-4 mr-1" />
                Limpar Assinatura
              </Button>
            </div>
            <div className="p-3 bg-transparent border border-slate-200 rounded-lg text-sm text-slate-700">
              <AlertTriangle className="h-4 w-4 inline mr-1 text-slate-500" />
              <strong>Importante:</strong> A impressão do documento completo só será permitida após a assinatura do cliente.
              Documentos não assinados serão marcados como &quot;PENDENTE DE ASSINATURA&quot;.
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { setSignatureDialog({ open: false, project: null, role: 'client' }); setSignatureSignerName(''); }}>
              Cancelar
            </Button>
            <Button onClick={handleSignature} data-testid="confirm-signature-btn" className="bg-green-600 hover:bg-green-700">
              <CheckCircle className="h-4 w-4 mr-2" />
              Guardar Assinatura
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CHECKLIST DIALOG */}
      <Dialog open={checklistDialog.open} onOpenChange={(open) => !open && setChecklistDialog({ open: false, project: null })}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-blue-500" />
              Checklist Técnico
            </DialogTitle>
            <DialogDescription>
              {checklistDialog.project?.project_number} - {checklistDialog.project?.title}
            </DialogDescription>
          </DialogHeader>
          {checklistDialog.project && (
            <div className="space-y-2">
              {(checklistDialog.project.checklist_items || []).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ClipboardList className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Checklist vazio</p>
                </div>
              ) : (
                checklistDialog.project.checklist_items.map((item) => (
                  <div 
                    key={item.id} 
                    className={`flex items-start gap-3 p-3 rounded-lg border ${item.is_completed ? 'bg-green-50 border-green-200' : 'bg-white'}`}
                  >
                    <Checkbox
                      checked={item.is_completed}
                      onCheckedChange={(checked) => handleChecklistToggle(checklistDialog.project.id, item.id, checked)}
                    />
                    <div className="flex-1">
                      <p className={item.is_completed ? 'line-through text-muted-foreground' : ''}>
                        {item.description}
                      </p>
                      {item.is_completed && item.completed_by && (
                        <p className="text-xs text-green-600 mt-1">
                          <CheckCircle className="h-3 w-3 inline mr-1" />
                          {item.completed_by} - {formatDate(item.completed_at)}
                        </p>
                      )}
                    </div>
                  </div>
                ))
              )}
              {(() => {
                const progress = getChecklistProgress(checklistDialog.project);
                return progress.total > 0 && (
                  <div className="pt-4 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Progresso</span>
                      <span className="text-sm">{progress.completed}/{progress.total} ({progress.percent}%)</span>
                    </div>
                    <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 transition-all"
                        style={{ width: `${progress.percent}%` }}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setChecklistDialog({ open: false, project: null })}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PRINT DIALOG - BLOQUEIO SEM ASSINATURA/EXECUÇÃO */}
      <Dialog open={printDialog.open} onOpenChange={(open) => !open && setPrintDialog({ open: false, project: null, canPrint: false })}>
        <DialogContent className="max-w-md" aria-describedby="project-print-dialog-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5" />
              Impressão de Documento
            </DialogTitle>
            <DialogDescription id="project-print-dialog-description">
              Verificação de assinatura e detalhes de execução antes da impressão do relatório da obra.
            </DialogDescription>
          </DialogHeader>
          {printDialog.project && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="font-medium">{printDialog.project.project_number}</p>
                <p className="text-sm text-muted-foreground">{printDialog.project.title}</p>
              </div>
              
              {printDialog.canPrint ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                  <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="text-green-800 font-medium">Documento pronto para impressão</p>
                  <p className="text-sm text-green-600">Assinatura e detalhes de execução completos</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg text-center">
                    <AlertTriangle className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                    <p className="text-orange-800 font-medium">Documento incompleto</p>
                    <p className="text-sm text-orange-600">{printDialog.reason}</p>
                  </div>
                  
                  {/* Campos em falta */}
                  {printDialog.missingFields?.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-amber-800 mb-2">Campos em falta:</p>
                      <ul className="text-xs text-amber-700 space-y-1">
                        {printDialog.missingFields.map((field, i) => (
                          <li key={i} className="flex items-center gap-1">
                            <XCircle className="h-3 w-3" />
                            {field === 'execution_date' && 'Data de execução'}
                            {field === 'start_time' && 'Hora de início'}
                            {field === 'end_time' && 'Hora de fim'}
                            {field === 'execution_location' && 'Local de execução'}
                            {field === 'technicians_names' && 'Técnico responsável'}
                          </li>
                        ))}
                      </ul>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="mt-2 w-full"
                        onClick={() => {
                          setPrintDialog({ open: false, project: null, canPrint: false });
                          openExecutionDialog(printDialog.project);
                        }}
                      >
                        <Timer className="h-3 w-3 mr-1" />
                        Preencher Detalhes de Execução
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={() => setPrintDialog({ open: false, project: null, canPrint: false })}>
              Cancelar
            </Button>
            {!printDialog.canPrint && printDialog.allowPendingPrint && (
              <Button 
                variant="outline" 
                className="text-orange-600"
                onClick={() => handlePrint(true)}
              >
                <FileText className="h-4 w-4 mr-2" />
                Imprimir Pendente
              </Button>
            )}
            {printDialog.canPrint && (
              <Button onClick={() => handlePrint(false)} className="bg-green-600 hover:bg-green-700">
                <Printer className="h-4 w-4 mr-2" />
                Imprimir Documento
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EXECUTION DETAILS DIALOG - DIRETIVA P0 */}
      <Dialog open={executionDialog.open} onOpenChange={(open) => !open && setExecutionDialog({ open: false, project: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Timer className="h-5 w-5 text-blue-500" />
              Detalhes de Execução
            </DialogTitle>
            <DialogDescription>
              {executionDialog.project?.project_number} - Campos obrigatórios para impressão
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <AlertTriangle className="h-4 w-4 inline mr-1 text-blue-600" />
              <strong>Obrigatório:</strong> Preencha todos os campos para poder imprimir o documento final.
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Data de Execução *</Label>
                <Input
                  type="date"
                  value={executionForm.execution_date}
                  onChange={(e) => setExecutionForm({ ...executionForm, execution_date: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Data Final *</Label>
                <Input
                  type="date"
                  value={executionForm.execution_end_date}
                  onChange={(e) => setExecutionForm({ ...executionForm, execution_end_date: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Hora Início *</Label>
                <Input
                  type="time"
                  value={executionForm.start_time}
                  onChange={(e) => setExecutionForm({ ...executionForm, start_time: e.target.value })}
                />
              </div>
              <div>
                <Label className="text-xs">Hora Fim *</Label>
                <Input
                  type="time"
                  value={executionForm.end_time}
                  onChange={(e) => setExecutionForm({ ...executionForm, end_time: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label className="text-xs">Loja de Origem da Obra</Label>
              <Select
                value={executionForm.store_id || ''}
                onValueChange={(value) => setExecutionForm({ ...executionForm, store_id: value })}
              >
                <SelectTrigger data-testid="execution-store-select-trigger">
                  <SelectValue placeholder="Seleccione a loja" />
                </SelectTrigger>
                <SelectContent>
                  {availableStores.map((store) => (
                    <SelectItem key={store.id} value={store.id} data-testid={`execution-store-option-${store.id}`}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">Local de Execução *</Label>
              <Input
                value={executionForm.execution_location}
                onChange={(e) => setExecutionForm({ ...executionForm, execution_location: e.target.value })}
                placeholder="Ex: Sede do cliente, Veículo ABC-123"
              />
            </div>

            <div>
              <Label className="text-xs">Ordem de Serviço (opcional)</Label>
              <Input
                value={executionForm.work_order}
                onChange={(e) => setExecutionForm({ ...executionForm, work_order: e.target.value })}
                placeholder="OS-2025/001"
              />
            </div>

            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="execution-work-segments-panel">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <Label className="text-xs">Jornadas efectivas de trabalho</Label>
                  <p className="text-[11px] text-slate-500">Use estas linhas para considerar apenas o tempo em que o técnico esteve efectivamente no local.</p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addWorkSegment} data-testid="execution-add-work-segment-button">
                  <Plus className="h-3 w-3 mr-1" /> Adicionar jornada
                </Button>
              </div>
              <div className="space-y-2">
                {(executionForm.work_segments || []).map((segment, index) => (
                  <div key={`execution-work-segment-${index}`} className="grid grid-cols-1 md:grid-cols-[1fr_120px_120px_40px] gap-2 items-end">
                    <div>
                      <Label className="text-[11px]">Data</Label>
                      <Input type="date" value={segment.work_date || ''} onChange={(e) => updateWorkSegment(index, 'work_date', e.target.value)} data-testid={`execution-work-segment-date-${index}`} />
                    </div>
                    <div>
                      <Label className="text-[11px]">Entrada</Label>
                      <Input type="time" value={segment.start_time || ''} onChange={(e) => updateWorkSegment(index, 'start_time', e.target.value)} data-testid={`execution-work-segment-start-${index}`} />
                    </div>
                    <div>
                      <Label className="text-[11px]">Saída</Label>
                      <Input type="time" value={segment.end_time || ''} onChange={(e) => updateWorkSegment(index, 'end_time', e.target.value)} data-testid={`execution-work-segment-end-${index}`} />
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeWorkSegment(index)} disabled={(executionForm.work_segments || []).length <= 1} data-testid={`execution-remove-work-segment-${index}`}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Técnico(s) Responsável(eis) *</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addTechnician} data-testid="execution-add-technician-button">
                  <Plus className="h-3 w-3 mr-1" /> Adicionar
                </Button>
              </div>
              <div className="space-y-2">
                {executionForm.technicians_names.map((tech, index) => (
                  <div key={`execution-technician-${index}`} className="flex gap-2">
                    <Input
                      value={tech}
                      onChange={(e) => updateTechnician(index, e.target.value)}
                      placeholder="Nome do técnico"
                      className="flex-1"
                      data-testid={`execution-technician-input-${index}`}
                    />
                    {executionForm.technicians_names.length > 1 && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeTechnician(index)} data-testid={`execution-remove-technician-button-${index}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs">Observações Finais (opcional)</Label>
              <Textarea
                value={executionForm.final_observations}
                onChange={(e) => setExecutionForm({ ...executionForm, final_observations: e.target.value })}
                placeholder="Observações sobre a execução do serviço..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExecutionDialog({ open: false, project: null })}>
              Cancelar
            </Button>
            <Button onClick={handleSaveExecution} className="bg-blue-600 hover:bg-blue-700">
              <Save className="h-4 w-4 mr-2" />
              Guardar Detalhes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* TYPE-SPECIFIC FIELDS DIALOG - DIRETIVA P0 */}
      <Dialog open={typeFieldsDialog.open} onOpenChange={(open) => !open && setTypeFieldsDialog({ open: false, project: null })}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby="project-type-fields-dialog-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-purple-500" />
              Campos Específicos - {PROJECT_TYPES[typeFieldsDialog.project?.project_type]?.label || 'Serviço'}
            </DialogTitle>
            <DialogDescription id="project-type-fields-dialog-description">
              {typeFieldsDialog.project?.project_number} - Detalhes técnicos específicos
            </DialogDescription>
          </DialogHeader>
          
          {typeFieldsDialog.project && (
            <div className="space-y-4">
              {/* GPS / Frota */}
              {typeFieldsDialog.project.project_type === 'gps_fleet' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-blue-600 font-medium">
                    <Truck className="h-5 w-5" />
                    Sistema GPS / Gestão de Frota
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Tipo de Operação</Label>
                      <Select value={typeFields.operation_type || 'installation'} onValueChange={(v) => setTypeFields({ ...typeFields, operation_type: v })}>
                        <SelectTrigger data-testid="gps-type-fields-operation-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{GPS_OPERATION_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    {(typeFields.operation_type || 'installation') === 'other' && (
                      <div className="md:col-span-2">
                        <Label className="text-xs">Descrição manual *</Label>
                        <Input value={typeFields.operation_other_description || ''} onChange={(e) => setTypeFields({ ...typeFields, operation_other_description: e.target.value })} data-testid="gps-type-fields-other-description" />
                      </div>
                    )}
                    {GPS_INSTALLATION_OPERATION_TYPES.includes(typeFields.operation_type || 'installation') && (
                      <div>
                        <Label className="text-xs">Tipo de Instalação</Label>
                        <Select value={typeFields.installation_type || 'new_installation'} onValueChange={(v) => setTypeFields({ ...typeFields, installation_type: v })}>
                          <SelectTrigger data-testid="gps-type-fields-installation-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{GPS_INSTALLATION_TYPE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                    {(typeFields.operation_type || 'installation') === 'uninstallation' && (
                      <div>
                        <Label className="text-xs">Campo de Desinstalação</Label>
                        <Select value={typeFields.uninstallation_type || 'permanent_uninstallation'} onValueChange={(v) => setTypeFields({ ...typeFields, uninstallation_type: v })}>
                          <SelectTrigger data-testid="gps-type-fields-uninstallation-select"><SelectValue /></SelectTrigger>
                          <SelectContent>{GPS_UNINSTALLATION_TYPE_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Matrícula</Label>
                      <Input
                        value={typeFields.vehicle_plate || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, vehicle_plate: e.target.value })}
                        placeholder="ABC-123-MZ"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Modelo Veículo</Label>
                      <Input
                        value={typeFields.vehicle_model || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, vehicle_model: e.target.value })}
                        placeholder="Toyota Hilux 2023"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">IMEI do GPS</Label>
                      <Input
                        value={typeFields.imei_gps || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, imei_gps: e.target.value })}
                        placeholder="359XXXXXXXXX"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Nº SIM Card</Label>
                      <Input
                        value={typeFields.sim_card_number || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, sim_card_number: e.target.value })}
                        placeholder="+258 84 XXX XXXX"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Local da Intervenção</Label>
                      <Input
                        value={typeFields.intervention_site || typeFields.installation_location || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, intervention_site: e.target.value, installation_location: e.target.value })}
                        placeholder="Veículo, oficina, cliente, etc."
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Estado antes da intervenção</Label>
                      <Input
                        value={typeFields.equipment_condition_before || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, equipment_condition_before: e.target.value })}
                        placeholder="Operacional, avariado, removido, etc."
                        data-testid="gps-type-fields-condition-before-input"
                      />
                    </div>
                  </div>

                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2" data-testid="gps-installation-checklist-preview">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-blue-900">
                        Questionário dinâmico por operação GPS / Frota
                      </p>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={applyChecklistTemplate}
                          onCheckedChange={(checked) => setApplyChecklistTemplate(Boolean(checked))}
                          data-testid="gps-apply-checklist-template-checkbox"
                        />
                        <Label className="text-xs">Aplicar este questionário</Label>
                      </div>
                    </div>
                    <p className="text-[11px] text-blue-800">
                      Se já existirem itens concluídos, o sistema preserva os dados actuais e não substitui checklist automaticamente.
                    </p>
                    <p className="text-[11px] font-medium text-blue-900">
                      Contexto activo: {getGpsOptionLabel(GPS_OPERATION_OPTIONS, typeFields.operation_type || 'installation')}
                    </p>
                    <ul className="space-y-1 text-xs text-blue-900">
                      {buildGpsQuestionnairePreview(typeFields)
                        .slice(0, 10)
                        .map((question, index) => (
                          <li key={question} className="flex items-start gap-2" data-testid={`gps-checklist-preview-item-${index + 1}`}>
                            <span className="mt-0.5">•</span>
                            <span>{question}</span>
                          </li>
                        ))}
                    </ul>
                  </div>

                  <div>
                    <Label className="text-xs">Teste de Comunicação</Label>
                    <Select value={typeFields.communication_test || 'pending'} onValueChange={(v) => setTypeFields({ ...typeFields, communication_test: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">⏳ Pendente</SelectItem>
                        <SelectItem value="ok">✓ OK - Funcional</SelectItem>
                        <SelectItem value="nok">✗ NOK - Com Problemas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Cliente final / Departamento</Label><Input value={typeFields.final_customer_department || ''} onChange={(e) => setTypeFields({ ...typeFields, final_customer_department: e.target.value })} /></div>
                    <div>
                      <Label className="text-xs">Prioridade</Label>
                      <Select value={typeFields.priority || 'normal'} onValueChange={(v) => setTypeFields({ ...typeFields, priority: v })}>
                        <SelectTrigger data-testid="gps-type-fields-priority-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Baixa</SelectItem>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="high">Alta</SelectItem>
                          <SelectItem value="critical">Crítica</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label className="text-xs">Observações técnicas</Label><Textarea rows={3} value={typeFields.technical_observations || ''} onChange={(e) => setTypeFields({ ...typeFields, technical_observations: e.target.value })} /></div>
                    <div><Label className="text-xs">Peças / materiais usados</Label><Textarea rows={3} value={typeFields.materials_used || ''} onChange={(e) => setTypeFields({ ...typeFields, materials_used: e.target.value })} /></div>
                    <div><Label className="text-xs">Estado final do serviço</Label><Input value={typeFields.final_service_status || ''} onChange={(e) => setTypeFields({ ...typeFields, final_service_status: e.target.value })} /></div>
                    <div><Label className="text-xs">Notas</Label><Textarea rows={3} value={typeFields.notes || ''} onChange={(e) => setTypeFields({ ...typeFields, notes: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 rounded-md border p-3"><Checkbox checked={!!typeFields.requires_travel} onCheckedChange={(v) => setTypeFields({ ...typeFields, requires_travel: v === true })} /><span className="text-sm">Necessita deslocação?</span></div>
                    <div className="flex items-center gap-2 rounded-md border p-3"><Checkbox checked={!!typeFields.requires_final_tests} onCheckedChange={(v) => setTypeFields({ ...typeFields, requires_final_tests: v === true })} /><span className="text-sm">Necessita testes finais?</span></div>
                    <div className="flex items-center gap-2 rounded-md border p-3"><Checkbox checked={!!typeFields.uninstall_requires_reinstallation} onCheckedChange={(v) => setTypeFields({ ...typeFields, uninstall_requires_reinstallation: v === true })} /><span className="text-sm">Necessita reinstalação posterior?</span></div>
                    <div className="flex items-center gap-2 rounded-md border p-3"><Checkbox checked={!!typeFields.requires_return} onCheckedChange={(v) => setTypeFields({ ...typeFields, requires_return: v === true })} /><span className="text-sm">Necessita retorno?</span></div>
                  </div>
                </div>
              )}

              {/* CCTV */}
              {typeFieldsDialog.project.project_type === 'cctv' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-purple-600 font-medium">
                    <Video className="h-5 w-5" />
                    CCTV / Videovigilância
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Nº Câmaras *</Label>
                      <Input
                        type="number"
                        value={typeFields.cameras_count || 0}
                        onChange={(e) => setTypeFields({ ...typeFields, cameras_count: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Tipo DVR/NVR *</Label>
                      <Input
                        value={typeFields.dvr_nvr_type || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, dvr_nvr_type: e.target.value })}
                        placeholder="DVR 8CH"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Modelo</Label>
                      <Input
                        value={typeFields.dvr_nvr_model || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, dvr_nvr_model: e.target.value })}
                        placeholder="Hikvision DS-..."
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Armazenamento</Label>
                      <Input
                        value={typeFields.storage_capacity || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, storage_capacity: e.target.value })}
                        placeholder="2TB"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Configuração IP</Label>
                      <Input
                        value={typeFields.ip_config || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, ip_config: e.target.value })}
                        placeholder="192.168.1.XXX"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Teste de Gravação</Label>
                      <Select value={typeFields.recording_test || 'pending'} onValueChange={(v) => setTypeFields({ ...typeFields, recording_test: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">⏳ Pendente</SelectItem>
                          <SelectItem value="ok">✓ OK - Funcional</SelectItem>
                          <SelectItem value="nok">✗ NOK - Com Problemas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2 pt-5">
                      <Checkbox
                        checked={typeFields.remote_access_configured || false}
                        onCheckedChange={(checked) => setTypeFields({ ...typeFields, remote_access_configured: checked })}
                      />
                      <Label className="text-xs">Acesso Remoto Configurado</Label>
                    </div>
                  </div>
                </div>
              )}

              {/* Combustível */}
              {typeFieldsDialog.project.project_type === 'fuel_management' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-amber-600 font-medium">
                    <Fuel className="h-5 w-5" />
                    Gestão de Combustíveis
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Tipo de Sensor *</Label>
                      <Input
                        value={typeFields.sensor_type || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, sensor_type: e.target.value })}
                        placeholder="Capacitivo, Ultrassónico"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Modelo do Sensor</Label>
                      <Input
                        value={typeFields.sensor_model || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, sensor_model: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Capacidade Tanque (L) *</Label>
                      <Input
                        type="number"
                        value={typeFields.tank_capacity || 0}
                        onChange={(e) => setTypeFields({ ...typeFields, tank_capacity: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Tipo Combustível</Label>
                      <Select value={typeFields.fuel_type || ''} onValueChange={(v) => setTypeFields({ ...typeFields, fuel_type: v })}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="diesel">Diesel</SelectItem>
                          <SelectItem value="gasolina">Gasolina</SelectItem>
                          <SelectItem value="gas">Gás</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Pontos Calibração</Label>
                      <Input
                        type="number"
                        value={typeFields.calibration_points || 0}
                        onChange={(e) => setTypeFields({ ...typeFields, calibration_points: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={typeFields.calibration_done || false}
                      onCheckedChange={(checked) => setTypeFields({ ...typeFields, calibration_done: checked })}
                    />
                    <Label className="text-xs">Calibração Concluída</Label>
                  </div>
                </div>
              )}

              {/* Controlo de Acesso */}
              {typeFieldsDialog.project.project_type === 'access_control' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-600 font-medium">
                    <Lock className="h-5 w-5" />
                    Controlo de Acesso
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Nº Leitores *</Label>
                      <Input
                        type="number"
                        value={typeFields.readers_count || 0}
                        onChange={(e) => setTypeFields({ ...typeFields, readers_count: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Tipo Leitor *</Label>
                      <Select value={typeFields.reader_type || ''} onValueChange={(v) => setTypeFields({ ...typeFields, reader_type: v })}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="biometric">Biométrico</SelectItem>
                          <SelectItem value="card">Cartão</SelectItem>
                          <SelectItem value="pin">PIN</SelectItem>
                          <SelectItem value="mixed">Misto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Nº Portas</Label>
                      <Input
                        type="number"
                        value={typeFields.doors_count || 0}
                        onChange={(e) => setTypeFields({ ...typeFields, doors_count: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Modelo Controlador</Label>
                      <Input
                        value={typeFields.controller_model || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, controller_model: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Utilizadores Registados</Label>
                      <Input
                        type="number"
                        value={typeFields.users_registered || 0}
                        onChange={(e) => setTypeFields({ ...typeFields, users_registered: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={typeFields.software_installed || false}
                      onCheckedChange={(checked) => setTypeFields({ ...typeFields, software_installed: checked })}
                    />
                    <Label className="text-xs">Software de Gestão Instalado</Label>
                  </div>
                </div>
              )}

              {/* Redes */}
              {typeFieldsDialog.project.project_type === 'networking' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-cyan-600 font-medium">
                    <Wifi className="h-5 w-5" />
                    Redes e Infraestrutura
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Tipo de Rede *</Label>
                      <Select value={typeFields.network_type || ''} onValueChange={(v) => setTypeFields({ ...typeFields, network_type: v })}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LAN">LAN</SelectItem>
                          <SelectItem value="WiFi">WiFi</SelectItem>
                          <SelectItem value="Fiber">Fibra Óptica</SelectItem>
                          <SelectItem value="Mixed">Mista</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Tipo de Cabo</Label>
                      <Select value={typeFields.cable_type || ''} onValueChange={(v) => setTypeFields({ ...typeFields, cable_type: v })}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CAT5e">CAT5e</SelectItem>
                          <SelectItem value="CAT6">CAT6</SelectItem>
                          <SelectItem value="CAT6A">CAT6A</SelectItem>
                          <SelectItem value="Fiber">Fibra</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Metros de Cabo</Label>
                      <Input
                        type="number"
                        value={typeFields.cable_meters || 0}
                        onChange={(e) => setTypeFields({ ...typeFields, cable_meters: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Nº Equipamentos Activos</Label>
                      <Input
                        type="number"
                        value={typeFields.equipment_count || 0}
                        onChange={(e) => setTypeFields({ ...typeFields, equipment_count: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Teste de Conectividade</Label>
                      <Select value={typeFields.connectivity_test || 'pending'} onValueChange={(v) => setTypeFields({ ...typeFields, connectivity_test: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">⏳ Pendente</SelectItem>
                          <SelectItem value="ok">✓ OK - Funcional</SelectItem>
                          <SelectItem value="nok">✗ NOK - Com Problemas</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Resultado Teste Velocidade</Label>
                      <Input
                        value={typeFields.speed_test_result || ''}
                        onChange={(e) => setTypeFields({ ...typeFields, speed_test_result: e.target.value })}
                        placeholder="100 Mbps"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Outros */}
              {typeFieldsDialog.project.project_type === 'other' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-gray-600 font-medium">
                    <Package className="h-5 w-5" />
                    Outros Serviços
                  </div>
                  <div>
                    <Label className="text-xs">Notas e Observações</Label>
                    <Textarea
                      value={typeFields.notes || ''}
                      onChange={(e) => setTypeFields({ ...typeFields, notes: e.target.value })}
                      placeholder="Detalhes específicos deste serviço..."
                      rows={4}
                    />
                  </div>
                </div>
              )}

              {/* Notas para todos os tipos */}
              {typeFieldsDialog.project.project_type !== 'other' && (
                <div>
                  <Label className="text-xs">Notas Adicionais</Label>
                  <Textarea
                    value={typeFields.notes || ''}
                    onChange={(e) => setTypeFields({ ...typeFields, notes: e.target.value })}
                    placeholder="Observações técnicas..."
                    rows={2}
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeFieldsDialog({ open: false, project: null })}>
              Cancelar
            </Button>
            <Button onClick={handleSaveTypeFields} className="bg-purple-600 hover:bg-purple-700">
              <Save className="h-4 w-4 mr-2" />
              Guardar Campos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UPDATE STORE DIALOG */}
      <Dialog
        open={storeDialog.open}
        onOpenChange={(open) => {
          if (!open) setStoreDialog({ open: false, project: null, storeId: '' });
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="h-5 w-5 text-indigo-600" />
              Loja da Obra
            </DialogTitle>
            <DialogDescription>
              {storeDialog.project?.project_number} - seleccione a loja de origem.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Loja *</Label>
            <Select
              value={storeDialog.storeId || ''}
              onValueChange={(value) => setStoreDialog((prev) => ({ ...prev, storeId: value }))}
            >
              <SelectTrigger data-testid="project-store-update-select-trigger">
                <SelectValue placeholder="Seleccione a loja" />
              </SelectTrigger>
              <SelectContent>
                {availableStores.map((store) => (
                  <SelectItem key={store.id} value={store.id} data-testid={`project-store-update-option-${store.id}`}>
                    {store.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStoreDialog({ open: false, project: null, storeId: '' })}>Cancelar</Button>
            <Button onClick={handleUpdateProjectStore} data-testid="project-store-update-confirm-button">Guardar Loja</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CUSTOMER PORTAL LINK DIALOG */}
      <Dialog
        open={portalDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setPortalDialog({
              open: false,
              project: null,
              allowedEmailsRaw: '',
              expiresInDays: 7,
              generatedLink: '',
              generatedPath: '',
              expiresAt: '',
              creating: false,
            });
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-sky-600" />
              Acesso de Cliente por Link
            </DialogTitle>
            <DialogDescription>
              {portalDialog.project?.project_number} - o link só funciona para emails permitidos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Emails permitidos (separe por vírgula ou nova linha)</Label>
              <Textarea
                value={portalDialog.allowedEmailsRaw}
                onChange={(e) => setPortalDialog((prev) => ({ ...prev, allowedEmailsRaw: e.target.value }))}
                rows={3}
                data-testid="customer-portal-allowed-emails-input"
                placeholder="cliente@empresa.com, gestor@empresa.com"
              />
            </div>
            <div>
              <Label>Dias de validade</Label>
              <Input
                type="number"
                min={1}
                max={30}
                value={portalDialog.expiresInDays}
                onChange={(e) => setPortalDialog((prev) => ({ ...prev, expiresInDays: e.target.value }))}
                data-testid="customer-portal-expiry-days-input"
              />
            </div>
            <Button
              onClick={handleGeneratePublicLink}
              disabled={portalDialog.creating}
              className="w-full"
              data-testid="customer-portal-generate-link-button"
            >
              <Link2 className="h-4 w-4 mr-2" />
              {portalDialog.creating ? 'A gerar link...' : 'Gerar Link Seguro'}
            </Button>

            {portalDialog.generatedLink && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="customer-portal-generated-link-box">
                <p className="text-sm font-medium text-emerald-900">Link gerado</p>
                <p className="text-xs break-all text-emerald-800">{portalDialog.generatedLink}</p>
                {portalDialog.expiresAt && (
                  <p className="text-xs text-emerald-700">Expira em: {formatDate(portalDialog.expiresAt)}</p>
                )}
                <Button variant="outline" onClick={handleCopyPortalLink} data-testid="customer-portal-copy-link-button">
                  <Copy className="h-4 w-4 mr-2" /> Copiar Link
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* INTEGRITY CHECK DIALOG */}
      <Dialog open={integrityDialog.open} onOpenChange={(open) => !open && setIntegrityDialog({ open: false, loading: false, project: null, report: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              Check Completo de Conformidade
            </DialogTitle>
            <DialogDescription>
              {integrityDialog.project?.project_number} - validação técnica e operacional
            </DialogDescription>
          </DialogHeader>
          {integrityDialog.loading ? (
            <div className="py-8 text-center text-muted-foreground" data-testid="integrity-check-loading">A executar validações...</div>
          ) : integrityDialog.report ? (
            <div className="space-y-2" data-testid="integrity-check-results">
              {(integrityDialog.report.checks || []).map((check) => (
                <div key={check.code} className="rounded-lg border p-3 flex items-start justify-between gap-3" data-testid={`integrity-check-item-${check.code}`}>
                  <div>
                    <p className="text-sm font-medium">{check.label}</p>
                    <p className="text-xs text-muted-foreground">{check.details}</p>
                  </div>
                  <Badge className={check.status === 'pass' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}>
                    {check.status === 'pass' ? 'OK' : 'ATENÇÃO'}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center text-muted-foreground">Sem dados de validação.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
